import { displayAttribute } from "../i18n/LocalizedCopy";
import { formattingLocale } from "../i18n/formattingLocale";
import { LocalizedText, LocalizedElement, sourceAttribute, useSourceLanguage } from "../i18n/LocalizedCopy";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { buildAuthHeaders } from "../forum/authHeaders";
import ChatPanel from "../realtime/ChatPanel";
import TrustCompactSummary from "../trust/TrustCompactSummary";
import MemberAvatar, { publishMemberAvatarUpdate } from "./MemberAvatar";
import MemberRelationshipAction from "./MemberRelationshipAction";
import EternuaIcon from "./EternuaIcon";
import {
  isGameProtocolMessage,
} from "../realtime/chatGameProtocol";
import { useI18n } from "../i18n/LanguageContext";
import "./FriendsDock.css";
import "./MessengerInbox.css";

const LS_OUTGOING = "eternua_friendsdock_outgoing_v1";
const LS_ACCEPTED_SEEN = "eternua_friendsdock_accepted_seen_v1";
const LS_CHAT_UNREAD_SEEN = "eternua_friendsdock_chat_unread_seen_v1";

function safeJsonParse(raw, fallback) {
  try {
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch (_) {
    return fallback;
  }
}

function normalizeUsername(u) {
  return String(u || "").trim().toLowerCase();
}

function applyPresenceSnapshotToFriends(list, snapshot) {
  const rows = Array.isArray(list) ? list : [];
  const hasSnapshot = Boolean(snapshot?.received);
  if (!hasSnapshot) {
    return rows;
  }
  const chatOnline = snapshot?.chatOnline instanceof Set ? snapshot.chatOnline : new Set();
  const videoOnline = snapshot?.videoOnline instanceof Set ? snapshot.videoOnline : new Set();
  return rows.map((f) => {
    const uname = normalizeUsername(f?.username);
    const onlineChat = chatOnline.has(uname);
    const onlineVideo = videoOnline.has(uname);
    return {
      ...f,
      online_chat: onlineChat,
      online_video: onlineVideo,
      online: onlineChat || onlineVideo,
    };
  });
}

function sourceLabel(source) {
  const s = String(source || "").trim().toLowerCase();
  if (s === "animaux_adoption") return "Animaux";
  if (s === "animaux_garde") return "Garde d’animaux";
  if (s === "emploi") return "Emploi";
  if (s === "vendre") return "Vendre";
  if (s === "acheter") return "Acheter";
  if (s === "donner") return "Donner";
  if (s === "services") return "Services";
  if (s === "sortie") return "Sortie";
  if (s === "voyage") return "Voyage";
  if (s === "sport_loisir") return "Sport et loisir";
  if (s === "musique") return "Musique";
  if (s === "forum") return "Forum";
  return "Général";
}

function conversationCategoryLabel(category) {
  const labels = {
    general: "Général",
    achats: "Achats",
    services: "Services",
    voyages: "Voyages",
    animaux: "Animaux",
    emploi: "Emploi",
    sorties: "Sorties",
    sport: "Sport",
    musique: "Musique",
    forum: "Forum",
    groupes: "Groupes",
    event: "Événements",
    carpool: "Covoiturage",
    voyage: "Voyages",
    project: "Projets",
  };
  return labels[String(category || "general").toLowerCase()] || "Général";
}

function dedupeCalls(list, direction = "received") {
  const arr = Array.isArray(list) ? list : [];
  const filtered = arr.filter((n) =>
    direction === "sent"
      ? String(n?.direction || "") === "sent"
      : String(n?.direction || "received") !== "sent"
  );
  const sorted = [...filtered].sort((a, b) => {
    const ta = Date.parse(String(a?.updated_at || a?.created_at || "")) || 0;
    const tb = Date.parse(String(b?.updated_at || b?.created_at || "")) || 0;
    if (tb !== ta) return tb - ta;
    const ia = Number(a?.id || 0);
    const ib = Number(b?.id || 0);
    return ib - ia;
  });
  const seen = new Set();
  const out = [];
  sorted.forEach((n) => {
    const who =
      String(
        n?.other_user?.username ||
          n?.from_user?.username ||
          n?.to_user?.username ||
          ""
      )
        .trim()
        .toLowerCase() || "unknown";
    const key = [
      who,
      String(n?.kind || ""),
      direction,
    ].join("|");
    if (seen.has(key)) return;
    seen.add(key);
    out.push(n);
  });
  return out;
}

function isRecentRinging(notif, ttlMs = 120000) {
  if (!notif) return false;
  if (String(notif?.status || "").toLowerCase() !== "ringing") return false;
  const created = Date.parse(String(notif?.created_at || ""));
  if (!Number.isFinite(created)) return false;
  return Date.now() - created <= ttlMs;
}

function callKindLabel(kind, roomSlug = "") {
  const k = String(kind || "").trim().toLowerCase();
  if (k === "private_video" && String(roomSlug || "") === "audio") {
    return "Appel audio";
  }
  if (k === "private_chat") return "Appel chat";
  if (k === "private_video") return "Appel vidéo";
  if (k === "video_room") return "Invitation visio groupe";
  return "Appel";
}

function Avatar({ user }) {
  useSourceLanguage();
  return (
    <MemberAvatar
      member={user}
      size="sm"
      className="friends-dock__avatar friends-dock__avatar--img"
      context="friends_dock"
    />
  );
}

export default function FriendsDock({
  messengerOnly = false,
  messengerOpen,
  messengerExpanded = false,
  onMessengerExpandedChange,
  onMessengerOpenChange,
}) {
  useSourceLanguage();
  const ConversationOptions = messengerOnly ? "details" : React.Fragment;
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useI18n();

  const isAuthed = Boolean(user);

  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("online"); // online | requests | accepted

  useEffect(() => {
    const path = String(location?.pathname || "");
    if (path.startsWith("/video/private/") || path.startsWith("/calls")) {
      setActiveTab("calls");
    }
  }, [location?.pathname]);

  const [chatOpenInternal, setChatOpenInternal] = useState(false);
  const chatOpen = typeof messengerOpen === "boolean" ? messengerOpen : chatOpenInternal;
  const [chatErr, setChatErr] = useState("");
  const [conversations, setConversations] = useState([]);
  const [conversationSearch, setConversationSearch] = useState("");
  const messengerSearchRef = useRef(null);
  const messengerPanelRef = useRef(null);
  const [conversationFilter, setConversationFilter] = useState("all");
  const [conversationCategory, setConversationCategory] = useState("all");
  const [activityGroups, setActivityGroups] = useState([]);
  const [friendSuggestions, setFriendSuggestions] = useState([]);
  const [activeChatUser, setActiveChatUser] = useState(null); // username
  const [chatMessages, setChatMessages] = useState([]);
  const [appointmentOpen, setAppointmentOpen] = useState(false);
  const [appointmentBusy, setAppointmentBusy] = useState(false);
  const [appointmentForm, setAppointmentForm] = useState({
    scheduled_for: "",
    public_place_label: "",
    note: "",
  });
  const [chatToast, setChatToast] = useState(""); // short notification
  const [confirmDeleteChatOpen, setConfirmDeleteChatOpen] = useState(false);
  const [deleteChatBusy, setDeleteChatBusy] = useState(false);
  const [deleteChatErr, setDeleteChatErr] = useState("");
  const [deleteChatTarget, setDeleteChatTarget] = useState("");
  const wsRef = useRef(null);
  const notifyWsRef = useRef(null);
  const notifyReconnectTimerRef = useRef(null);
  const notifyManualCloseRef = useRef(false);
  const ringtoneCtxRef = useRef(null);
  const ringtoneIntervalRef = useRef(null);
  const presenceSnapshotRef = useRef({
    chatOnline: new Set(),
    videoOnline: new Set(),
    received: false,
  });
  const chatOpenRef = useRef(false);
  const chatSeenIdsRef = useRef(new Set());
  const chatSeenLooseRef = useRef(new Set());

  const setChatOpen = useCallback((nextValue) => {
    const currentValue = chatOpenRef.current;
    const resolvedValue = typeof nextValue === "function"
      ? Boolean(nextValue(currentValue))
      : Boolean(nextValue);
    setChatOpenInternal(resolvedValue);
    onMessengerOpenChange?.(resolvedValue);
  }, [onMessengerOpenChange]);

  const [friends, setFriends] = useState([]);
  const [incoming, setIncoming] = useState([]);
  const [acceptedNotifs, setAcceptedNotifs] = useState([]); // [{ username, ts }]
  const [callNotifs, setCallNotifs] = useState([]); // missed/ringing calls persisted
  const [incomingCall, setIncomingCall] = useState(null); // realtime ringing call
  const [outgoingCall, setOutgoingCall] = useState(null); // local pending call sent
  const [error, setError] = useState("");
  const [busyIds, setBusyIds] = useState(() => new Set());

  const lastLoadedAtRef = useRef(0);
  const dismissedIncomingCallIdsRef = useRef(new Set());

  useEffect(() => {
    chatOpenRef.current = Boolean(chatOpen);
  }, [chatOpen]);

  useEffect(() => {
    if (!messengerOnly || !chatOpen) return undefined;
    document.documentElement.classList.add("eternua-messenger-open");
    document.body.classList.add("eternua-messenger-open");
    return () => {
      document.documentElement.classList.remove("eternua-messenger-open");
      document.body.classList.remove("eternua-messenger-open");
    };
  }, [chatOpen, messengerOnly]);

  useEffect(() => {
    if (!messengerExpanded) setConversationSearch("");
    if (!chatOpen || !messengerOnly || !messengerExpanded) return undefined;
    const trigger = document.activeElement;
    messengerPanelRef.current?.focus();
    return () => { if (trigger?.isConnected) trigger.focus(); };
  }, [chatOpen, messengerOnly, messengerExpanded]);

  const onlineFriends = useMemo(() => {
    const list = Array.isArray(friends) ? friends : [];
    return list.filter((f) => f?.online || f?.online_chat || f?.online_video);
  }, [friends]);

  const callUnseenCount = useMemo(() => {
    const list = Array.isArray(callNotifs) ? callNotifs : [];
    return list.filter((n) => !n?.seen).length;
  }, [callNotifs]);
  const receivedCalls = useMemo(() => {
    return dedupeCalls(callNotifs, "received");
  }, [callNotifs]);
  const sentCalls = useMemo(() => {
    return dedupeCalls(callNotifs, "sent");
  }, [callNotifs]);

  const counts = useMemo(() => {
    return {
      online: onlineFriends.length,
      incoming: Array.isArray(incoming) ? incoming.length : 0,
      accepted: Array.isArray(acceptedNotifs) ? acceptedNotifs.length : 0,
      calls: callUnseenCount,
    };
  }, [onlineFriends.length, incoming, acceptedNotifs, callUnseenCount]);

  const currentVideoRoomSlug = useMemo(() => {
    const p = String(location?.pathname || "");
    if (p.startsWith("/video/room/")) {
      const rest = p.slice("/video/room/".length);
      const slug = rest.split("/")[0];
      return slug || "";
    }
    return "";
  }, [location?.pathname]);

  const load = useCallback(async () => {
    if (!isAuthed) return;

    // Basic throttle: avoid double-fetch if two triggers land together.
    const now = Date.now();
    if (now - lastLoadedAtRef.current < 1500) return;
    lastLoadedAtRef.current = now;

    setError("");
    try {
      const [friendsRes, requestsRes] = await Promise.all([
        fetch("/api/auth/friends/", { headers: buildAuthHeaders() }),
        fetch("/api/auth/friend-requests/", { headers: buildAuthHeaders() }),
      ]);

      if (!friendsRes.ok || !requestsRes.ok) {
        throw new Error("Impossible de charger les amis.");
      }

      const [friendsData, requestsData] = await Promise.all([
        friendsRes.json(),
        requestsRes.json(),
      ]);

      const nextFriends = Array.isArray(friendsData) ? friendsData : [];
      const nextIncoming = Array.isArray(requestsData?.incoming)
        ? requestsData.incoming
        : [];
      const nextOutgoing = Array.isArray(requestsData?.outgoing)
        ? requestsData.outgoing
        : [];

      setFriends(
        applyPresenceSnapshotToFriends(nextFriends, presenceSnapshotRef.current)
      );
      setIncoming(nextIncoming);

      // "Amis acceptes" notification: infer when an outgoing request disappears and the user is now a friend.
      const prevOutgoing = safeJsonParse(localStorage.getItem(LS_OUTGOING), []);
      const prevOutgoingUsers = new Set(
        (Array.isArray(prevOutgoing) ? prevOutgoing : [])
          .map((r) => normalizeUsername(r?.to_username))
          .filter(Boolean)
      );

      const nextOutgoingUsers = new Set(
        nextOutgoing
          .map((r) => normalizeUsername(r?.to_username))
          .filter(Boolean)
      );

      const friendsUsers = new Set(
        nextFriends.map((f) => normalizeUsername(f?.username)).filter(Boolean)
      );

      const seenAccepted = new Set(
        (safeJsonParse(localStorage.getItem(LS_ACCEPTED_SEEN), []) || [])
          .map(normalizeUsername)
          .filter(Boolean)
      );

      const newlyAccepted = [];
      for (const uname of prevOutgoingUsers) {
        if (
          !nextOutgoingUsers.has(uname) &&
          friendsUsers.has(uname) &&
          !seenAccepted.has(uname)
        ) {
          newlyAccepted.push({ username: uname, ts: Date.now() });
          seenAccepted.add(uname);
        }
      }

      if (newlyAccepted.length > 0) {
        setAcceptedNotifs((prev) =>
          [...newlyAccepted, ...(Array.isArray(prev) ? prev : [])].slice(0, 20)
        );
        localStorage.setItem(
          LS_ACCEPTED_SEEN,
          JSON.stringify(Array.from(seenAccepted))
        );
      }

      localStorage.setItem(LS_OUTGOING, JSON.stringify(nextOutgoing));
    } catch (e) {
      setError(e?.message || "Erreur reseau.");
    }
  }, [isAuthed]);

  const playMessageDing = useCallback(() => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      const now = ctx.currentTime;
      osc.type = "triangle";
      osc.frequency.setValueAtTime(1240, now);
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(0.06, now + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.14);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now);
      osc.stop(now + 0.15);
      window.setTimeout(() => {
        try {
          ctx.close();
        } catch (_) {
          // no-op
        }
      }, 220);
    } catch (_) {
      // no-op
    }
  }, []);

  const loadConversations = useCallback(async () => {
    if (!isAuthed) return;
    setChatErr("");
    try {
      const res = await fetch("/api/chat/conversations/?limit=30", {
        headers: buildAuthHeaders(),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(data?.detail || "Impossible de charger les discussions.");
      const list = Array.isArray(data) ? data : [];
      setConversations(list);

      // Toast when new unread arrives (only if panel closed)
      const totalUnread = list.reduce(
        (sum, c) => sum + (Number(c?.unread_count || 0) || 0),
        0
      );
      const prevUnread = Number(localStorage.getItem(LS_CHAT_UNREAD_SEEN) || 0) || 0;
      if (!chatOpenRef.current && totalUnread > prevUnread) {
        const newest = list.find((c) => (Number(c?.unread_count || 0) || 0) > 0);
        const who = newest?.other?.display_name || newest?.other?.username || "une amie";
        playMessageDing();
        setChatToast(`Nouveau message de ${who}`);
        window.setTimeout(() => setChatToast(""), 5000);
      }
      localStorage.setItem(LS_CHAT_UNREAD_SEEN, String(totalUnread));
    } catch (e) {
      setChatErr(e?.message || "Erreur reseau.");
      setConversations([]);
    }
  }, [isAuthed, playMessageDing]);

  const loadSocialDiscovery = useCallback(async () => {
    if (!isAuthed) return;
    try {
      const [groupsResponse, suggestionsResponse] = await Promise.all([
        fetch("/api/chat/rooms/groups/", { headers: buildAuthHeaders() }),
        fetch("/api/auth/friends/suggestions/?limit=8", { headers: buildAuthHeaders() }),
      ]);
      const [groupsPayload, suggestionsPayload] = await Promise.all([
        groupsResponse.json().catch(() => []),
        suggestionsResponse.json().catch(() => ({})),
      ]);
      setActivityGroups(groupsResponse.ok && Array.isArray(groupsPayload) ? groupsPayload : []);
      setFriendSuggestions(
        suggestionsResponse.ok && Array.isArray(suggestionsPayload?.results)
          ? suggestionsPayload.results
          : []
      );
    } catch (_) {
      setActivityGroups([]);
      setFriendSuggestions([]);
    }
  }, [isAuthed]);

  const loadCallNotifs = useCallback(async () => {
    if (!isAuthed) return;
    try {
      const res = await fetch("/api/chat/calls/notifications/?limit=20", {
        headers: buildAuthHeaders(),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) return;
      setCallNotifs(Array.isArray(data) ? data : []);
    } catch (_) {
      // no-op
    }
  }, [isAuthed]);

  const stopIncomingRingtone = useCallback(() => {
    if (ringtoneIntervalRef.current) {
      window.clearInterval(ringtoneIntervalRef.current);
      ringtoneIntervalRef.current = null;
    }
    if (ringtoneCtxRef.current) {
      try {
        ringtoneCtxRef.current.close();
      } catch (_) {
        // no-op
      }
      ringtoneCtxRef.current = null;
    }
  }, []);

  const startIncomingRingtone = useCallback(() => {
    stopIncomingRingtone();
    let audioCtx = null;
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      audioCtx = new Ctx();
      ringtoneCtxRef.current = audioCtx;
    } catch (_) {
      return;
    }

    const playPulse = () => {
      try {
        if (!ringtoneCtxRef.current) return;
        const ctx = ringtoneCtxRef.current;
        const now = ctx.currentTime;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.setValueAtTime(920, now);
        gain.gain.setValueAtTime(0.0001, now);
        gain.gain.exponentialRampToValueAtTime(0.09, now + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.33);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(now);
        osc.stop(now + 0.35);
      } catch (_) {
        // no-op
      }
    };

    playPulse();
    ringtoneIntervalRef.current = window.setInterval(playPulse, 1200);
  }, [stopIncomingRingtone]);

  const closeChatSocket = useCallback(() => {
    try {
      if (wsRef.current) wsRef.current.close();
    } catch (_) {
      // no-op
    }
    wsRef.current = null;
  }, []);

  const splitProtocolMessages = useCallback((list) => {
    const rows = Array.isArray(list) ? list : [];
    const visible = [];
    const protocol = [];
    rows.forEach((m) => {
      const txt = String(m?.contenu || m?.message || m?.text || "");
      if (isGameProtocolMessage(txt)) {
        protocol.push(m);
      } else {
        visible.push(m);
      }
    });
    return { visible, protocol };
  }, []);

  const getStoredToken = () => {
    const read = (store, key) => {
      try {
        const raw = store.getItem(key);
        if (!raw) return null;
        if (raw.startsWith("\"") && raw.endsWith("\"")) return JSON.parse(raw);
        return raw;
      } catch (_) {
        return null;
      }
    };
    return (
      read(localStorage, "authToken") ||
      read(localStorage, "token") ||
      read(localStorage, "auth_token") ||
      read(sessionStorage, "authToken") ||
      read(sessionStorage, "token") ||
      read(sessionStorage, "auth_token")
    );
  };

  // Notifications WS: receive call invites anywhere in the app.
  useEffect(() => {
    if (!isAuthed) {
      notifyManualCloseRef.current = true;
      if (notifyReconnectTimerRef.current) {
        window.clearTimeout(notifyReconnectTimerRef.current);
        notifyReconnectTimerRef.current = null;
      }
      try {
        if (notifyWsRef.current) notifyWsRef.current.close();
      } catch (_) {
        // no-op
      }
      notifyWsRef.current = null;
      setIncomingCall(null);
      return;
    }

    const token = getStoredToken();
    const protocole = window.location.protocol === "https:" ? "wss" : "ws";
    let backendHost =
      process.env.REACT_APP_BACKEND_WS_HOST ||
      (window.location.hostname === "localhost"
        ? "localhost:8000"
        : window.location.host);
    const configuredApiBase =
      process.env.REACT_APP_API_BASE_URL_LOCAL ||
      process.env.REACT_APP_API_BASE_URL ||
      "";
    if (!process.env.REACT_APP_BACKEND_WS_HOST && configuredApiBase) {
      try {
        const configuredApiUrl = new URL(configuredApiBase, window.location.origin);
        if (["http:", "https:"].includes(configuredApiUrl.protocol)) {
          backendHost = configuredApiUrl.host;
        }
      } catch (_) {
        // Keep the same-origin/default host when the optional URL is invalid.
      }
    }
    const path = "/ws/notify/";
    const withToken = token ? `${path}?token=${encodeURIComponent(token)}` : path;
    const wsUrl = `${protocole}://${backendHost}${withToken}`;
    let cancelled = false;
    notifyManualCloseRef.current = false;

    const clearReconnect = () => {
      if (notifyReconnectTimerRef.current) {
        window.clearTimeout(notifyReconnectTimerRef.current);
        notifyReconnectTimerRef.current = null;
      }
    };

    const scheduleReconnect = (delayMs = 1200) => {
      if (cancelled || notifyManualCloseRef.current || !isAuthed) return;
      clearReconnect();
      notifyReconnectTimerRef.current = window.setTimeout(() => {
        notifyReconnectTimerRef.current = null;
        connectNotifySocket();
      }, delayMs);
    };

    const connectNotifySocket = () => {
      if (cancelled || notifyManualCloseRef.current || !isAuthed) return;
      const existing = notifyWsRef.current;
      if (
        existing &&
        (existing.readyState === WebSocket.OPEN ||
          existing.readyState === WebSocket.CONNECTING)
      ) {
        return;
      }
      let ws = null;
      try {
        ws = new WebSocket(wsUrl);
        notifyWsRef.current = ws;
      } catch (_) {
        scheduleReconnect(2000);
        return;
      }

      ws.onopen = () => {
        clearReconnect();
        load();
        loadCallNotifs();
      };

      ws.onmessage = (event) => {
        try {
          const payload = JSON.parse(event.data);
          if (payload?.type === "member_avatar_updated") {
            if (!payload?.requires_refresh) {
              publishMemberAvatarUpdate(payload);
            }
            // Private avatar URLs are short-lived and bound to the recipient.
            // Force a reload so this browser receives its own signed URL.
            lastLoadedAtRef.current = 0;
            load();
            window.dispatchEvent(new CustomEvent("eternua:member-profile-updated", {
              detail: payload,
            }));
          } else if (payload?.type === "member_profile_updated") {
            const changedUsername = normalizeUsername(payload?.username);
            const identityPatch = {
              display_name: String(payload?.display_name || payload?.username || "").trim(),
            };
            setFriends((previous) =>
              (Array.isArray(previous) ? previous : []).map((friend) =>
                normalizeUsername(friend?.username) === changedUsername
                  ? { ...friend, ...identityPatch }
                  : friend
              )
            );
            setConversations((previous) =>
              (Array.isArray(previous) ? previous : []).map((conversation) =>
                normalizeUsername(conversation?.other?.username) === changedUsername
                  ? {
                      ...conversation,
                      other: { ...conversation.other, ...identityPatch },
                    }
                  : conversation
              )
            );
            if (!payload?.requires_refresh) {
              publishMemberAvatarUpdate({
                ...payload,
                type: "member_avatar_updated",
              });
            }
            lastLoadedAtRef.current = 0;
            load();
            window.dispatchEvent(new CustomEvent("eternua:member-profile-updated", {
              detail: payload,
            }));
          } else if (payload?.type === "presence_snapshot") {
            const chatOnline = new Set(
              (Array.isArray(payload?.chat_online) ? payload.chat_online : [])
                .map(normalizeUsername)
                .filter(Boolean)
            );
            const videoOnline = new Set(
              (Array.isArray(payload?.video_online) ? payload.video_online : [])
                .map(normalizeUsername)
                .filter(Boolean)
            );
            presenceSnapshotRef.current = { chatOnline, videoOnline, received: true };
            setFriends((prev) =>
              applyPresenceSnapshotToFriends(prev, presenceSnapshotRef.current)
            );
          } else if (payload?.type === "call_invite") {
            setIncomingCall(payload);
            startIncomingRingtone();
            const who =
              payload?.from_user?.display_name ||
              payload?.from_user?.username ||
              "une amie";
            setChatToast(`${callKindLabel(payload?.kind, payload?.room_slug)} de ${who}`);
            window.setTimeout(() => setChatToast(""), 5000);
            loadCallNotifs();
          } else if (payload?.type === "call_invite_accepted") {
            setOutgoingCall(null);
            const who =
              payload?.by_user?.display_name ||
              payload?.by_user?.username ||
              "Une amie";
            setChatToast(`${who} a rejoint l'appel`);
            window.setTimeout(() => setChatToast(""), 5000);
            try {
              window.dispatchEvent(
                new CustomEvent("eternua:video-invite-accepted", {
                  detail: {
                    id: Number(payload?.id || 0),
                    kind: String(payload?.kind || ""),
                    username: String(payload?.by_user?.username || ""),
                  },
                })
              );
            } catch (_) {
              // no-op
            }
          } else if (payload?.type === "call_invite_cancelled") {
            const cancelledId = Number(payload?.id || 0);
            stopIncomingRingtone();
            setIncomingCall((prev) => {
              if (!prev) return prev;
              return Number(prev?.id || 0) === cancelledId ? null : prev;
            });
            setCallNotifs((prev) =>
              (Array.isArray(prev) ? prev : []).filter((n) => Number(n?.id || 0) !== cancelledId)
            );
            const who =
              payload?.by_user?.display_name ||
              payload?.by_user?.username ||
              "Une amie";
            setChatToast(`${who} a annulé l'appel`);
            window.setTimeout(() => setChatToast(""), 3500);
          } else if (payload?.type === "call_invite_declined") {
            setOutgoingCall(null);
            const who =
              payload?.by_user?.display_name ||
              payload?.by_user?.username ||
              "Une amie";
            setChatToast(`${who} a refusé l'appel`);
            window.setTimeout(() => setChatToast(""), 5000);
          }
        } catch (_) {
          // no-op
        }
      };

      ws.onclose = () => {
        if (notifyWsRef.current === ws) notifyWsRef.current = null;
        if (!cancelled && !notifyManualCloseRef.current) {
          scheduleReconnect(1500);
        }
      };

      ws.onerror = () => {
        try {
          ws.close();
        } catch (_) {
          // no-op
        }
      };
    };

    connectNotifySocket();

    return () => {
      cancelled = true;
      notifyManualCloseRef.current = true;
      clearReconnect();
      const ws = notifyWsRef.current;
      notifyWsRef.current = null;
      if (!ws) return;
      try {
        ws.close();
      } catch (_) {
        // no-op
      }
    };
  }, [isAuthed, load, loadCallNotifs, startIncomingRingtone, stopIncomingRingtone]);

  useEffect(() => {
    return () => {
      stopIncomingRingtone();
    };
  }, [stopIncomingRingtone]);

  useEffect(() => {
    const p = String(location?.pathname || "");
    const inVideo =
      p.startsWith("/video/private/") ||
      p.startsWith("/video/room/") ||
      p === "/temple-lia-video" ||
      p.startsWith("/video/temple/");
    if (inVideo) {
      stopIncomingRingtone();
    }
  }, [location?.pathname, stopIncomingRingtone]);

  useEffect(() => {
    if (!isAuthed) return;
    const current = incomingCall || outgoingCall;
    const mode = incomingCall ? "incoming" : outgoingCall ? "outgoing" : "";
    try {
      window.dispatchEvent(
        new CustomEvent("eternua:incoming-call-state", {
          detail: {
            active: Boolean(current),
            mode,
            status: String(current?.status || ""),
            kind: String(current?.kind || ""),
            id: current?.id || null,
            join_url: String(current?.join_url || ""),
            from_user: current?.from_user || null,
            to_user: current?.to_user || null,
          },
        })
      );
    } catch (_) {
      // no-op
    }
  }, [incomingCall, isAuthed, outgoingCall]);

  useEffect(() => {
    if (!isAuthed) return;
    if (incomingCall) return;
    const ringing = (Array.isArray(callNotifs) ? callNotifs : []).find((n) => {
      const direction = String(n?.direction || "received").toLowerCase();
      if (direction === "sent") return false;
      if (!isRecentRinging(n)) return false;
      const id = Number(n?.id || 0);
      return !dismissedIncomingCallIdsRef.current.has(id);
    });
    if (!ringing) return;
    setIncomingCall({
      id: ringing?.id || null,
      kind: String(ringing?.kind || "private_video"),
      room_slug: String(ringing?.room_slug || ""),
      status: "ringing",
      join_url: String(ringing?.join_url || ""),
      from_user: ringing?.from_user || ringing?.other_user || null,
      to_user: ringing?.to_user || null,
    });
    startIncomingRingtone();
  }, [callNotifs, incomingCall, isAuthed, startIncomingRingtone]);

  // Best-effort busy state: let the backend know when we're currently in video.
  const lastVideoStateRef = useRef(null);
  useEffect(() => {
    if (!isAuthed) return;
    const p = String(location?.pathname || "");
    const inVideo =
      p.startsWith("/video/private/") ||
      p.startsWith("/video/room/") ||
      p === "/temple-lia-video" ||
      p.startsWith("/video/temple/");

    const next = inVideo ? "in_video" : "idle";
    if (lastVideoStateRef.current === next) return;
    lastVideoStateRef.current = next;

    try {
      if (notifyWsRef.current && notifyWsRef.current.readyState === WebSocket.OPEN) {
        notifyWsRef.current.send(
          JSON.stringify({ type: "call_state", payload: { state: next } })
        );
      }
    } catch (_) {
      // no-op
    }
  }, [isAuthed, location?.pathname]);

  const openChatWith = useCallback(
    async (username) => {
      if (!isAuthed) return;
      const uname = String(username || "").trim();
      if (!uname) return;
      setChatErr("");
      setActiveChatUser(uname);
      setChatMessages([]);
      chatSeenIdsRef.current = new Set();
      chatSeenLooseRef.current = new Set();

      // Load history first
      try {
        const res = await fetch(
          `/api/chat/private/${encodeURIComponent(uname)}/history/?limit=120`,
          { headers: buildAuthHeaders() }
        );
        const data = await res.json().catch(() => null);
        if (res.ok) {
          const list = Array.isArray(data) ? data : [];
          for (const m of list) {
            if (m?.id) chatSeenIdsRef.current.add(m.id);
            const k = `${m?.auteur || ""}|${m?.timestamp || ""}|${m?.contenu || ""}`.slice(
              0,
              220
            );
            if (k) chatSeenLooseRef.current.add(k);
          }
          const split = splitProtocolMessages(list);
          setChatMessages(split.visible);
        }
        // history marks as read server-side, refresh list to update unread badge
        loadConversations();
        window.dispatchEvent(new CustomEvent("eternua:chat-updated"));
      } catch (_) {
        // no-op (history is optional)
      }

      // Replace socket
      closeChatSocket();
      const token = getStoredToken();
      const protocole = window.location.protocol === "https:" ? "wss" : "ws";
      const backendHost =
        process.env.REACT_APP_BACKEND_WS_HOST ||
        (window.location.hostname === "localhost"
          ? "localhost:8000"
          : window.location.host);
      const path = `/ws/chat/private/${encodeURIComponent(uname)}/`;
      const withToken = token ? `${path}?token=${encodeURIComponent(token)}` : path;
      const wsUrl = `${protocole}://${backendHost}${withToken}`;

      try {
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onmessage = (event) => {
          try {
            const payload = JSON.parse(event.data);
            if (payload?.type === "message") {
              const pid = payload?.id;
              if (pid && chatSeenIdsRef.current.has(pid)) return;
              const k = `${payload?.auteur || ""}|${payload?.timestamp || ""}|${payload?.contenu || ""}`.slice(
                0,
                220
              );
              if (!pid && k && chatSeenLooseRef.current.has(k)) return;
              if (pid) chatSeenIdsRef.current.add(pid);
              if (k) chatSeenLooseRef.current.add(k);
              if (isGameProtocolMessage(payload?.contenu || payload?.message || payload?.text || "")) {
                return;
              } else {
                setChatMessages((prev) => [
                  ...(Array.isArray(prev) ? prev : []),
                  payload,
                ]);
              }
              // Keep unread indicators in sync everywhere
              loadConversations();
              window.dispatchEvent(new CustomEvent("eternua:chat-updated"));
            }
          } catch (_) {
            // no-op
          }
        };

        ws.onclose = () => {
          if (wsRef.current === ws) wsRef.current = null;
        };
      } catch (e) {
        setChatErr(e?.message || "Impossible d'ouvrir la discussion.");
      }
    },
    [closeChatSocket, isAuthed, loadConversations, splitProtocolMessages]
  );

  useEffect(() => {
    const openRequestedConversation = (event) => {
      const username = String(event?.detail?.username || "").trim();
      if (!isAuthed || !username) return;
      setChatOpen(true);
      openChatWith(username);
    };
    window.addEventListener("eternua:open-messenger", openRequestedConversation);
    return () => window.removeEventListener("eternua:open-messenger", openRequestedConversation);
  }, [isAuthed, openChatWith, setChatOpen]);

  useEffect(() => {
    const refreshFriendshipState = () => {
      if (!isAuthed) return;
      lastLoadedAtRef.current = 0;
      load();
      loadConversations();
    };
    window.addEventListener("eternua:friendships-updated", refreshFriendshipState);
    return () => window.removeEventListener("eternua:friendships-updated", refreshFriendshipState);
  }, [isAuthed, load, loadConversations]);

  const sendChatMessage = useCallback(
    (payload) => {
      if (!isAuthed) return;
      const contenu = payload?.contenu || "";
      if (!contenu?.trim() && !payload?.media_url && !payload?.gif_url) return;
      const uname = String(activeChatUser || "").trim();
      if (!uname) return;

      // Always send via REST (offline-friendly + guarantees UI update).
      setChatErr("");
      fetch(`/api/chat/private/${encodeURIComponent(uname)}/send/`, {
        method: "POST",
        headers: buildAuthHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({
          contenu,
          media_url: payload?.media_url || "",
          media_type: payload?.media_type || "",
          gif_url: payload?.gif_url || "",
        }),
      })
        .then(async (res) => {
          const data = await res.json().catch(() => ({}));
          if (!res.ok) throw new Error(data?.detail || "Envoi impossible.");
          const pid = data?.id;
          if (pid && chatSeenIdsRef.current.has(pid)) return null;
          const k = `${data?.auteur || ""}|${data?.timestamp || ""}|${data?.contenu || ""}`.slice(
            0,
            220
          );
          if (!pid && k && chatSeenLooseRef.current.has(k)) return null;
          if (pid) chatSeenIdsRef.current.add(pid);
          if (k) chatSeenLooseRef.current.add(k);
          if (isGameProtocolMessage(data?.contenu || data?.message || data?.text || "")) {
            return null;
          } else {
            setChatMessages((prev) => [...(Array.isArray(prev) ? prev : []), data]);
          }
          try {
            window.dispatchEvent(
              new CustomEvent("eternua:private-chat-message", {
                detail: {
                  peer: uname,
                  message: data,
                },
              })
            );
          } catch (_) {
            // no-op
          }
          loadConversations();
          window.dispatchEvent(new CustomEvent("eternua:chat-updated"));
          return null;
        })
        .catch((e) => setChatErr(e?.message || "Envoi impossible."));
    },
    [activeChatUser, isAuthed, loadConversations]
  );

  const confirmDeleteDiscussion = useCallback((username) => {
    const uname = String(username || "").trim();
    if (!uname) return;
    setDeleteChatErr("");
    setDeleteChatTarget(uname);
    setConfirmDeleteChatOpen(true);
  }, []);

  const deleteDiscussion = useCallback(async () => {
    const uname = String(deleteChatTarget || activeChatUser || "").trim();
    if (!uname || deleteChatBusy) return;
    setDeleteChatErr("");
    setDeleteChatBusy(true);
    try {
      const res = await fetch(`/api/chat/private/${encodeURIComponent(uname)}/clear/`, {
        method: "POST",
        headers: buildAuthHeaders({ "Content-Type": "application/json" }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || "Suppression impossible.");

      setConversations((prev) =>
        (Array.isArray(prev) ? prev : []).filter(
          (c) =>
            String(c?.other?.username || "").trim().toLowerCase() !==
            uname.toLowerCase()
        )
      );
      if (String(activeChatUser || "").trim().toLowerCase() === uname.toLowerCase()) {
        setActiveChatUser(null);
        setChatMessages([]);
        closeChatSocket();
      }
      setConfirmDeleteChatOpen(false);
      setDeleteChatTarget("");
      setChatToast("Discussion supprimée.");
      window.setTimeout(() => setChatToast(""), 3000);
      await loadConversations();
      window.dispatchEvent(new CustomEvent("eternua:chat-updated"));
    } catch (e) {
      setDeleteChatErr(e?.message || "Suppression impossible.");
    } finally {
      setDeleteChatBusy(false);
    }
  }, [activeChatUser, closeChatSocket, deleteChatBusy, deleteChatTarget, loadConversations]);

  const refreshActiveChatHistory = useCallback(async () => {
    if (!isAuthed || !chatOpen) return;
    const uname = String(activeChatUser || "").trim();
    if (!uname) return;
    try {
      const res = await fetch(
        `/api/chat/private/${encodeURIComponent(uname)}/history/?limit=140`,
        { headers: buildAuthHeaders() }
      );
      const data = await res.json().catch(() => null);
      if (!res.ok) return;
      const list = Array.isArray(data) ? data : [];
      for (const m of list) {
        if (m?.id) chatSeenIdsRef.current.add(m.id);
        const k = `${m?.auteur || ""}|${m?.timestamp || ""}|${m?.contenu || ""}`.slice(0, 220);
        if (k) chatSeenLooseRef.current.add(k);
      }
      const split = splitProtocolMessages(list);
      setChatMessages(split.visible);
      loadConversations();
      window.dispatchEvent(new CustomEvent("eternua:chat-updated"));
    } catch (_) {
      // no-op
    }
  }, [activeChatUser, chatOpen, isAuthed, loadConversations, splitProtocolMessages]);

  // Keep call recovery independent of the friends API, including on reconnect.
  useEffect(() => {
    if (!isAuthed) return;
    const tick = () => {
      load();
      loadCallNotifs();
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") loadCallNotifs();
    };

    tick();
    const interval = setInterval(tick, 12000);
    window.addEventListener("focus", loadCallNotifs);
    window.addEventListener("online", loadCallNotifs);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", loadCallNotifs);
      window.removeEventListener("online", loadCallNotifs);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [isAuthed, load, loadCallNotifs]);

  useEffect(() => {
    if (!chatOpen) return;
    if (!isAuthed) return;
    loadConversations();
    loadSocialDiscovery();
  }, [chatOpen, isAuthed, loadConversations, loadSocialDiscovery]);

  useEffect(() => {
    if (!chatOpen || !isAuthed || !activeChatUser) return undefined;
    const tick = () => refreshActiveChatHistory();
    const interval = setInterval(tick, 6000);
    const onFocus = () => tick();
    const onVisibility = () => {
      if (document.visibilityState === "visible") tick();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [chatOpen, isAuthed, activeChatUser, refreshActiveChatHistory]);

  // Background polling for unread badge (even when panel is closed)
  useEffect(() => {
    if (!isAuthed) return;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      await loadConversations();
    };
    tick();
    const interval = setInterval(tick, 15000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [isAuthed, loadConversations]);

  useEffect(() => {
    // Close ws when dock chat closes or user logs out
    if (!chatOpen || !isAuthed) {
      closeChatSocket();
      setActiveChatUser(null);
      setChatMessages([]);
    }
  }, [chatOpen, isAuthed, closeChatSocket]);

  useEffect(() => {
    const onSyncedMessage = (event) => {
      const peer = String(event?.detail?.peer || "")
        .trim()
        .toLowerCase();
      const active = String(activeChatUser || "")
        .trim()
        .toLowerCase();
      const message = event?.detail?.message;
      if (!peer || !active || peer !== active || !message || typeof message !== "object") {
        return;
      }
      const txt = String(message?.contenu || message?.message || message?.text || "");
      if (isGameProtocolMessage(txt)) return;

      const pid = message?.id;
      if (pid && chatSeenIdsRef.current.has(pid)) return;
      const k = `${message?.auteur || ""}|${message?.timestamp || ""}|${message?.contenu || ""}`.slice(
        0,
        220
      );
      if (!pid && k && chatSeenLooseRef.current.has(k)) return;
      if (pid) chatSeenIdsRef.current.add(pid);
      if (k) chatSeenLooseRef.current.add(k);
      setChatMessages((prev) => [...(Array.isArray(prev) ? prev : []), message]);
    };
    window.addEventListener("eternua:private-chat-message", onSyncedMessage);
    return () =>
      window.removeEventListener("eternua:private-chat-message", onSyncedMessage);
  }, [activeChatUser]);

  // Escape closes panel
  useEffect(() => {
    if (!open && !chatOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        setChatOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, chatOpen, setChatOpen]);

  const respondRequest = async (id, action) => {
    if (!isAuthed) {
      navigate("/login");
      return;
    }
    if (!id) return;

    setError("");
    setBusyIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
    try {
      const res = await fetch(`/api/auth/friend-requests/${id}/respond/`, {
        method: "POST",
        headers: buildAuthHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ action }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.detail || "Impossible de repondre.");
      }

      setIncoming((prev) =>
        Array.isArray(prev) ? prev.filter((r) => r.id !== id) : prev
      );
      await load();
      if (action === "accept") {
        navigate(data?.conversation_url || "/amis");
        setOpen(false);
      }
    } catch (e) {
      setError(e?.message || "Erreur reseau.");
    } finally {
      setBusyIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const sendCallInvite = useCallback(
    async ({ toUsername, kind, roomSlug, openDirect = false }) => {
      if (!isAuthed) {
        navigate("/login");
        return;
      }
      const uname = String(toUsername || "").trim();
      if (!uname) return;
      const safeKind = String(kind || "private_video").trim();

      setError("");
      try {
        const res = await fetch("/api/chat/calls/invite/", {
          method: "POST",
          headers: buildAuthHeaders({ "Content-Type": "application/json" }),
          body: JSON.stringify({
            to_username: uname,
            kind: safeKind,
            room_slug: roomSlug || "",
          }),
        });
        let data = {};
        try {
          data = await res.json();
        } catch (_) {
          data = {};
        }
        if (!res.ok) {
          const fallback = data?.detail || `Invitation impossible (HTTP ${res.status}).`;
          throw new Error(fallback);
        }

        if (data?.status === "busy") {
          setOutgoingCall(null);
          setChatToast(data?.detail || "Cette personne est déjà en vidéo.");
        } else if (data?.status === "pending") {
          setOutgoingCall({
            id: data?.id || null,
            status: "pending",
            kind: safeKind,
            room_slug: roomSlug || "",
            to_user: { username: uname },
          });
          setChatToast(data?.detail || "Invitation déjà envoyée, en attente de réponse.");
        } else if (data?.status === "offline") {
          setOutgoingCall({
            id: data?.id || null,
            status: "offline",
            kind: safeKind,
            room_slug: roomSlug || "",
            to_user: { username: uname },
          });
          setChatToast(
            data?.detail ||
              "Cette personne n'est pas connectée. Elle verra un appel en absence."
          );
        } else {
          setOutgoingCall({
            id: data?.id || null,
            status: "ringing",
            kind: safeKind,
            room_slug: roomSlug || "",
            to_user: { username: uname },
          });
          setChatToast(data?.detail || "Invitation envoyée.");
        }
        if (openDirect && safeKind === "private_video" && data?.status !== "busy") {
          const query = new URLSearchParams();
          if (String(roomSlug || "").trim() === "audio") {
            query.set("mode", "audio");
          }
          if (data?.id) {
            query.set("outgoing_call", String(data.id));
          }
          const inviteQuery = query.toString();
          navigate(
            `/video/private/${encodeURIComponent(uname)}${
              inviteQuery ? `?${inviteQuery}` : ""
            }`
          );
        }
        window.setTimeout(() => setChatToast(""), 5500);
        loadCallNotifs();
      } catch (e) {
        setError(e?.message || "Erreur reseau.");
      }
    },
    [isAuthed, loadCallNotifs, navigate]
  );

  const clearAccepted = () => {
    setAcceptedNotifs([]);
  };

  const acceptIncomingCall = useCallback(async () => {
    if (!isAuthed) {
      navigate("/login");
      return;
    }
    const joinUrl = incomingCall?.join_url;
    const id = incomingCall?.id;
    if (!id && !joinUrl) return;
    let acceptedJoinUrl = joinUrl;
    try {
      if (id) {
        const response = await fetch(`/api/chat/calls/notifications/${id}/accept/`, {
          method: "POST",
          headers: buildAuthHeaders(),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(
            data?.detail || "L’appel n’a pas pu être accepté."
          );
        }
        if (data?.join_url) {
          acceptedJoinUrl = data.join_url;
        }
      }
    } catch (error) {
      setChatToast(
        error?.message || "L’appel n’a pas pu être accepté. Réessaie."
      );
      window.setTimeout(() => setChatToast(""), 4500);
      return;
    }
    if (id) dismissedIncomingCallIdsRef.current.add(Number(id));
    stopIncomingRingtone();
    setIncomingCall(null);
    setOutgoingCall(null);
    if (id) {
      setCallNotifs((prev) =>
        (Array.isArray(prev) ? prev : []).filter(
          (notification) => Number(notification?.id) !== Number(id)
        )
      );
    }
    if (acceptedJoinUrl) {
      const separator = acceptedJoinUrl.includes("?") ? "&" : "?";
      navigate(
        `${acceptedJoinUrl}${separator}accepted_call=${encodeURIComponent(id || "")}`
      );
    }
  }, [incomingCall, isAuthed, navigate, stopIncomingRingtone]);

  const declineIncomingCall = useCallback(async () => {
    if (!isAuthed) {
      navigate("/login");
      return;
    }
    const id = incomingCall?.id;
    if (id) dismissedIncomingCallIdsRef.current.add(Number(id));
    stopIncomingRingtone();
    setIncomingCall(null);
    setOutgoingCall(null);
    if (id) {
      setCallNotifs((prev) =>
        (Array.isArray(prev) ? prev : []).filter((n) => Number(n?.id) !== Number(id))
      );
    }
    try {
      if (id) {
        await fetch(`/api/chat/calls/notifications/${id}/decline/`, {
          method: "POST",
          headers: buildAuthHeaders(),
        });
      }
    } catch (_) {
      // no-op
    }
  }, [incomingCall, isAuthed, navigate, stopIncomingRingtone]);

  const cancelOutgoingCall = useCallback(async (callIdFromEvent = null) => {
    const callId = Number(callIdFromEvent || outgoingCall?.id || 0);
    if (callId > 0) {
      try {
        await fetch(`/api/chat/calls/notifications/${callId}/cancel/`, {
          method: "POST",
          headers: buildAuthHeaders(),
        });
      } catch (_) {
        // no-op
      }
      setCallNotifs((prev) =>
        (Array.isArray(prev) ? prev : []).filter((n) => Number(n?.id || 0) !== callId)
      );
    }
    setOutgoingCall(null);
    setChatToast("Appel annulé.");
    window.setTimeout(() => setChatToast(""), 2600);
  }, [outgoingCall]);

  const clearOneCallNotif = useCallback(
    async (notif) => {
      const id = Number(notif?.id || 0);
      if (!id) return;
      const direction = String(notif?.direction || "received").toLowerCase();
      const status = String(notif?.status || "").toLowerCase();
      try {
        if (direction === "sent" && status === "ringing") {
          await fetch(`/api/chat/calls/notifications/${id}/cancel/`, {
            method: "POST",
            headers: buildAuthHeaders(),
          });
        }
        await fetch(`/api/chat/calls/notifications/${id}/delete/`, {
          method: "POST",
          headers: buildAuthHeaders(),
        });
      } catch (_) {
        // no-op
      }
      setCallNotifs((prev) =>
        (Array.isArray(prev) ? prev : []).filter(
          (n) => Number(n?.id || 0) !== id
        )
      );
    },
    []
  );

  useEffect(() => {
    const onHeaderCallAction = (event) => {
      const action = String(event?.detail?.action || "").trim().toLowerCase();
      if (!action) return;
      if (action === "accept") {
        acceptIncomingCall();
        return;
      }
      if (action === "decline") {
        declineIncomingCall();
        return;
      }
      if (action === "cancel_outgoing") {
        cancelOutgoingCall(event?.detail?.id || null);
        return;
      }
      if (action === "open_outgoing") {
        const toUsername = String(
          event?.detail?.to_user?.username ||
            outgoingCall?.to_user?.username ||
            ""
        ).trim();
        if (!toUsername) return;
        setOutgoingCall(null);
        const kind = String(
          event?.detail?.kind || outgoingCall?.kind || "private_video"
        )
          .trim()
          .toLowerCase();
        if (kind === "private_chat") {
          navigate(`/chat/private/${encodeURIComponent(toUsername)}`);
        } else {
          navigate(`/video/private/${encodeURIComponent(toUsername)}`);
        }
      }
    };
    window.addEventListener("eternua:header-call-action", onHeaderCallAction);
    return () => window.removeEventListener("eternua:header-call-action", onHeaderCallAction);
  }, [acceptIncomingCall, cancelOutgoingCall, declineIncomingCall, navigate, outgoingCall]);

  useEffect(() => {
    const onOutgoingCallState = (event) => {
      const detail = event?.detail || {};
      if (!detail?.active) {
        setOutgoingCall(null);
        return;
      }
      setOutgoingCall({
        id: detail?.id || null,
        status: String(detail?.status || "ringing"),
        kind: String(detail?.kind || "private_video"),
        room_slug: String(detail?.room_slug || ""),
        to_user: detail?.to_user || null,
        join_url: String(detail?.join_url || ""),
      });
    };
    window.addEventListener("eternua:outgoing-call-state", onOutgoingCallState);
    return () =>
      window.removeEventListener("eternua:outgoing-call-state", onOutgoingCallState);
  }, []);

  const openTab = (tab) => {
    setActiveTab(tab);
    if (!isAuthed) {
      navigate("/login");
      return;
    }
    setOpen(true);
  };

  const showAcceptedSection = activeTab === "accepted" || acceptedNotifs.length > 0;
  const showRequestsSection = activeTab === "requests" || incoming.length > 0;
  const showCallsSection = activeTab === "calls" || callUnseenCount > 0;

  const chatUnreadTotal = useMemo(() => {
    return (Array.isArray(conversations) ? conversations : []).reduce(
      (sum, c) => sum + (Number(c?.unread_count || 0) || 0),
      0
    );
  }, [conversations]);

  const activeChatMeta = useMemo(() => {
    const uname = String(activeChatUser || "").trim();
    if (!uname) return null;
    const lower = uname.toLowerCase();

    const fromConversations = (Array.isArray(conversations) ? conversations : []).find(
      (c) => String(c?.other?.username || "").trim().toLowerCase() === lower
    )?.other;
    const fromFriends = (Array.isArray(friends) ? friends : []).find(
      (f) => String(f?.username || "").trim().toLowerCase() === lower
    );
    const info = fromConversations || fromFriends || { username: uname };
    return {
      username: String(info?.username || uname).trim(),
      displayName: String(info?.display_name || info?.username || uname).trim(),
      awayStatus: info?.away_status || null,
    };
  }, [activeChatUser, conversations, friends]);

  const visibleConversations = useMemo(() => {
    const rows = Array.isArray(conversations) ? conversations : [];
    return rows.filter((conversation) => {
      const query = conversationSearch.trim().toLocaleLowerCase("fr");
      if (query && ![conversation.other?.display_name, conversation.other?.username, conversation.last_message?.preview]
        .some((value) => String(value || "").toLocaleLowerCase("fr").includes(query))) return false;
      if (conversationCategory !== "all" && conversation?.category !== conversationCategory) return false;
      if (conversationFilter === "unread" && Number(conversation?.unread_count || 0) <= 0) return false;
      if (conversationFilter === "friends" && conversation?.relationship_kind !== "friend") return false;
      if (conversationFilter === "journeys" && !conversation?.settings?.journey_active) return false;
      if (conversationFilter === "groups") return false;
      return true;
    }).sort((first, second) => {
      const categoryOrder = conversationCategoryLabel(first?.category).localeCompare(
        conversationCategoryLabel(second?.category),
        "fr"
      );
      if (categoryOrder) return categoryOrder;
      const pinnedOrder = Number(Boolean(second?.settings?.pinned)) - Number(Boolean(first?.settings?.pinned));
      if (pinnedOrder) return pinnedOrder;
      return String(second?.updated_at || "").localeCompare(String(first?.updated_at || ""));
    });
  }, [conversationCategory, conversationFilter, conversations, conversationSearch]);

  const visibleActivityGroups = useMemo(() => {
    const rows = Array.isArray(activityGroups) ? activityGroups : [];
    if (conversationFilter !== "groups") return [];
    return rows.filter((group) => String(group.name || "").toLocaleLowerCase("fr").includes(conversationSearch.trim().toLocaleLowerCase("fr")));
  }, [activityGroups, conversationFilter, conversationSearch]);

  const conversationCategories = useMemo(() => Array.from(new Set(
    (Array.isArray(conversations) ? conversations : [])
      .map((conversation) => String(conversation?.category || "general"))
      .filter(Boolean)
  )), [conversations]);

  const activeConversation = useMemo(() => {
    const username = String(activeChatUser || "").trim().toLowerCase();
    return (Array.isArray(conversations) ? conversations : []).find(
      (conversation) => String(conversation?.other?.username || "").trim().toLowerCase() === username
    ) || null;
  }, [activeChatUser, conversations]);

  const updateConversationSetting = useCallback(async (field, value) => {
    if (!activeConversation?.id) return;
    setChatErr("");
    try {
      const response = await fetch(
        `/api/chat/conversations/${encodeURIComponent(activeConversation.id)}/settings/`,
        {
          method: "PATCH",
          headers: buildAuthHeaders({ "Content-Type": "application/json" }),
          body: JSON.stringify({ [field]: value }),
        }
      );
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.detail || "Réglage impossible.");
      if (field === "archived" && value) {
        setActiveChatUser(null);
        setChatMessages([]);
        closeChatSocket();
      }
      await loadConversations();
    } catch (requestError) {
      setChatErr(requestError?.message || "Réglage impossible.");
    }
  }, [activeConversation, closeChatSocket, loadConversations]);

  const proposeAppointment = useCallback(async (event) => {
    event.preventDefault();
    const username = String(activeChatMeta?.username || activeChatUser || "").trim();
    if (!username || appointmentBusy) return;
    setAppointmentBusy(true);
    setChatErr("");
    try {
      const response = await fetch("/api/chat/relationships/appointments/", {
        method: "POST",
        headers: buildAuthHeaders({ "Content-Type": "application/json" }),
        credentials: "include",
        body: JSON.stringify({ username, ...appointmentForm }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.detail || "Rendez-vous impossible.");
      setAppointmentOpen(false);
      setAppointmentForm({ scheduled_for: "", public_place_label: "", note: "" });
      setChatToast(`Rendez-vous proposé à @${username}.`);
      window.setTimeout(() => setChatToast(""), 3500);
    } catch (requestError) {
      setChatErr(requestError?.message || "Rendez-vous impossible.");
    } finally {
      setAppointmentBusy(false);
    }
  }, [activeChatMeta, activeChatUser, appointmentBusy, appointmentForm]);

  const declineTemporaryContact = useCallback(async () => {
    const accessId = activeConversation?.temporary_access?.id;
    if (!accessId) return;
    setChatErr("");
    try {
      const response = await fetch(
        `/api/chat/relationships/temporary/${encodeURIComponent(accessId)}/respond/`,
        {
          method: "POST",
          headers: buildAuthHeaders({ "Content-Type": "application/json" }),
          credentials: "include",
          body: JSON.stringify({ action: "decline" }),
        }
      );
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.detail || "Action impossible.");
      await loadConversations();
    } catch (requestError) {
      setChatErr(requestError?.message || "Action impossible.");
    }
  }, [activeConversation, loadConversations]);

  return (
    <div
      className={
        "friends-dock" +
        (open ? " friends-dock--open" : "") +
        (messengerOnly ? " friends-dock--messenger-only" : "")
      }
      aria-live="polite"
    >
      {isAuthed && incomingCall ? (
        <LocalizedElement elementType={"div"} copyAttributes={["aria-label"]} className="friends-dock__incoming-screen" role="dialog" aria-label={sourceAttribute("Appel entrant")}>
          <div className="friends-dock__incoming-screen-backdrop" />
          <div className="friends-dock__incoming-screen-card">
            <div className="friends-dock__incoming-screen-kicker"><LocalizedText source={"Appel entrant"} /></div>
            <div className="friends-dock__incoming-screen-title">
              {incomingCall?.from_user?.display_name ||
                incomingCall?.from_user?.username ||
                <LocalizedText source={"Une amie"} />}{" "}<LocalizedText source={"vous appelle"} /></div>
            <div className="friends-dock__incoming-screen-sub">
              {callKindLabel(incomingCall?.kind, incomingCall?.room_slug)}
            </div>
            <div className="friends-dock__incoming-topbar-actions">
              <button
                type="button"
                className="friends-dock__btn friends-dock__btn--danger"
                onClick={declineIncomingCall}
              ><LocalizedText source={"Raccrocher"} /></button>
              <button
                type="button"
                className="friends-dock__btn friends-dock__btn--primary"
                onClick={acceptIncomingCall}
              >
                {incomingCall?.kind === "private_chat"
                  ? <LocalizedText source={"Rejoindre le chat"} />
                  : incomingCall?.room_slug === "audio"
                    ? <LocalizedText source={"Rejoindre l’appel audio"} />
                    : <LocalizedText source={"Rejoindre l’appel vidéo"} />}
              </button>
            </div>
          </div>
        </LocalizedElement>
      ) : null}

      {isAuthed && !incomingCall && outgoingCall ? (
        <LocalizedElement elementType={"div"} copyAttributes={["aria-label"]}
          className="friends-dock__incoming-screen friends-dock__incoming-screen--outgoing"
          role="dialog"
          aria-label={sourceAttribute("Appel sortant en attente")}
        >
          <div className="friends-dock__incoming-screen-backdrop" />
          <div className="friends-dock__incoming-screen-card">
            <div className="friends-dock__incoming-screen-kicker"><LocalizedText source={"Appel sortant"} /></div>
            <div className="friends-dock__incoming-screen-title">
              {callKindLabel(outgoingCall?.kind, outgoingCall?.room_slug)}<LocalizedText source={" en attente"} /></div>
            <div className="friends-dock__incoming-screen-sub">
              {outgoingCall?.to_user?.display_name ||
                outgoingCall?.to_user?.username ||
                <LocalizedText source={"Votre amie"} />}{" "}
              {String(outgoingCall?.status || "").toLowerCase() === "offline"
                ? <LocalizedText source={"est hors ligne"} />
                : String(outgoingCall?.status || "").toLowerCase() === "busy"
                  ? <LocalizedText source={"est déjà en appel"} />
                  : <LocalizedText source={"reçoit votre invitation"} />}
            </div>
            <div className="friends-dock__incoming-topbar-actions">
              <button
                type="button"
                className="friends-dock__btn friends-dock__btn--danger"
                onClick={() => cancelOutgoingCall(outgoingCall?.id || null)}
              ><LocalizedText source={"Raccrocher"} /></button>
              <button
                type="button"
                className="friends-dock__btn friends-dock__btn--primary"
                onClick={() => {
                  const toUsername = String(
                    outgoingCall?.to_user?.username || ""
                  ).trim();
                  if (!toUsername) return;
                  const kind = String(outgoingCall?.kind || "private_video");
                  if (kind === "private_chat") {
                    navigate(`/chat/private/${encodeURIComponent(toUsername)}`);
                    return;
                  }
                  const audioQuery = String(outgoingCall?.room_slug || "") === "audio"
                    ? "?mode=audio"
                    : "";
                  navigate(`/video/private/${encodeURIComponent(toUsername)}${audioQuery}`);
                }}
              ><LocalizedText source={"Ouvrir l'appel"} /></button>
            </div>
          </div>
        </LocalizedElement>
      ) : null}

      {!messengerOnly ? <div
        className="friends-dock__bar"
        role="navigation"
        aria-label="Navigation footer Eternua"
      >
        <button
          type="button"
          className={"friends-dock__chatbtn" + (chatOpen ? " friends-dock__chatbtn--open" : "")}
          onClick={() => {
            if (!isAuthed) {
              navigate("/login");
              return;
            }
            setChatOpen((v) => !v);
          }}
          aria-label={
            chatOpen
              ? t("dock.close_discussions", "Fermer les discussions")
              : t("dock.open_discussions", "Ouvrir les discussions")
          }
        >
          <span className="friends-dock__chatbtn-ico" aria-hidden="true">💬</span>
          <span className="friends-dock__chatbtn-text">
            {<LocalizedText source={t("dock.discussions", "Discussions")} />}
          </span>
          {isAuthed && chatUnreadTotal > 0 ? (
            <span className="friends-dock__chatbtn-badge" aria-label={t("dock.unread_messages", "Nouveaux messages")}>
              {chatUnreadTotal > 99 ? "99+" : chatUnreadTotal}
            </span>
          ) : null}
        </button>

        <button
          type="button"
          className="friends-dock__logo"
          onClick={() => navigate("/")}
          aria-label={t("dock.back_home", "Retour a l'accueil")}
        >
          <span className="friends-dock__portal" aria-hidden="true">
            <span className="fd-ring fd-ring--outer" />
            <span className="fd-ring fd-ring--middle" />
            <span className="fd-ring fd-ring--inner" />
            <span className="fd-core" />
          </span>
          <span className="friends-dock__logo-text"><LocalizedText source={"∞ E T E R N U A"} /></span>
        </button>

        <div className="friends-dock__nav" aria-label={t("dock.friends_actions", "Actions amis")}>
                    <button
            type="button"
            className="friends-dock__nav-btn friends-dock__nav-btn--feed"
            onClick={() => {
              if (!isAuthed) {
                navigate("/login");
                return;
              }
              navigate("/amis/actu");
            }}
          >
            <span className="friends-dock__nav-label">{<LocalizedText source={t("dock.feed", "Actu")} />}</span>
          </button>

          <button
            type="button"
            className={
              "friends-dock__nav-btn friends-dock__nav-btn--music" +
              (String(location?.pathname || "") === "/music"
                ? " friends-dock__nav-btn--active"
                : "")
            }
            onClick={() => {
              navigate("/music");
            }}
            aria-label={t("nav.media.music", "Musique & clips")}
            title={t("nav.media.music", "Musique & clips")}
          >
            <span aria-hidden="true">♫</span>
            <span className="friends-dock__nav-label">{<LocalizedText source={t("dock.music", "Musique")} />}</span>
          </button>

          <button
            type="button"
            className="friends-dock__nav-btn friends-dock__nav-btn--feed"
            onClick={() => {
              navigate("/jeux");
            }}
          >
            <span className="friends-dock__nav-label"><LocalizedText source={"Jeux"} /></span>
          </button>

          <LocalizedElement elementType={"button"} copyAttributes={["aria-label","title"]}
            type="button"
            className={
              "friends-dock__nav-btn friends-dock__nav-btn--feed" +
              (String(location?.pathname || "") === "/civilisations-et"
                ? " friends-dock__nav-btn--active"
                : "")
            }
            onClick={() => {
              navigate("/civilisations-et");
            }}
            aria-label={sourceAttribute("Civilisations E.T.")}
            title={sourceAttribute("Civilisations E.T.")}
          >
            <span aria-hidden="true">✦</span>
            <span className="friends-dock__nav-label">E.T</span>
          </LocalizedElement>

          <button
            type="button"
            className={
              "friends-dock__nav-btn" +
              (activeTab === "online"
                ? " friends-dock__nav-btn--active"
                : "")
            }
            onClick={() => openTab("online")}
          >
            <span className="friends-dock__nav-label">{<LocalizedText source={t("dock.friends", "Amis")} />}</span>
            <span className="friends-dock__badge">{counts.online}</span>
          </button>

          <button
            type="button"
            className={
              "friends-dock__nav-btn" +
              (activeTab === "requests"
                ? " friends-dock__nav-btn--active"
                : "") +
              (counts.incoming > 0 ? " friends-dock__nav-btn--hot" : "")
            }
            onClick={() => openTab("requests")}
          >
            <span className="friends-dock__nav-label">{<LocalizedText source={t("dock.requests", "Demandes")} />}</span>
            <span className="friends-dock__badge">{counts.incoming}</span>
          </button>

          <button
            type="button"
            className={
              "friends-dock__nav-btn" +
              (activeTab === "accepted"
                ? " friends-dock__nav-btn--active"
                : "") +
              (counts.accepted > 0 ? " friends-dock__nav-btn--glow" : "")
            }
            onClick={() => openTab("accepted")}
          >
            <span className="friends-dock__nav-label">{<LocalizedText source={t("dock.new", "Nouveaux")} />}</span>
            <span className="friends-dock__badge">{counts.accepted}</span>
          </button>

          <button
            type="button"
            className={
              "friends-dock__nav-btn" +
              (activeTab === "calls" ? " friends-dock__nav-btn--active" : "") +
              (counts.calls > 0 ? " friends-dock__nav-btn--glow" : "")
            }
            onClick={() => openTab("calls")}
          >
            <span className="friends-dock__nav-label">{<LocalizedText source={t("dock.calls", "Appels")} />}</span>
            <span className="friends-dock__badge">{counts.calls}</span>
          </button>

          <button
            type="button"
            className="friends-dock__nav-btn friends-dock__nav-btn--salon"
            onClick={() => {
              if (!isAuthed) {
                navigate("/login");
                return;
              }
              navigate("/amis/salon");
            }}
          >
            <span className="friends-dock__nav-label">{<LocalizedText source={t("dock.lounge", "Salon amis")} />}</span>
          </button>

          <button
            type="button"
            className="friends-dock__nav-btn friends-dock__nav-btn--groupes"
            onClick={() => {
              if (!isAuthed) {
                navigate("/login");
                return;
              }
              navigate("/amis/groupes");
            }}
          >
            <span className="friends-dock__nav-label"><LocalizedText source={"Groupe d'amie"} /></span>
          </button>

          <button
            type="button"
            className="friends-dock__nav-btn friends-dock__nav-btn--parrainage"
            onClick={() => {
              if (!isAuthed) {
                navigate("/login");
                return;
              }
              navigate("/parrainage");
            }}
          >
            <span className="friends-dock__nav-label"><LocalizedText source={"Parrainage"} /></span>
          </button>

        </div>

        <button
          type="button"
          className={
            "friends-dock__burger" + (open ? " friends-dock__burger--open" : "")
          }
          onClick={() => {
            if (!isAuthed) {
              navigate("/login");
              return;
            }
            setActiveTab("online");
            setOpen((v) => !v);
          }}
          aria-label={open ? t("common.close", "Fermer") : t("common.open", "Ouvrir")}
          aria-expanded={open ? "true" : "false"}
        >
          <span />
          <span />
          <span />
        </button>
      </div> : null}

      {chatOpen && isAuthed ? (
        <div
          ref={messengerPanelRef}
          tabIndex={-1}
          id={messengerOnly ? "eternua-mini-messenger" : undefined}
          className={
            "friends-dock__chatpanel" +
            (messengerOnly ? " friends-dock__chatpanel--compact" : "") +
            (messengerOnly && messengerExpanded ? " friends-dock__chatpanel--expanded" : "") +
            (messengerOnly && activeChatUser ? " friends-dock__chatpanel--conversation-open" : "")
          }
          role="dialog"
          aria-label={t("dock.discussions", "Discussions")}
        >
          <div className="friends-dock__chatpanel-top">
            <div className="friends-dock__chatpanel-title">
              <span className="friends-dock__chatpanel-kicker"><LocalizedText source={"Messagerie Eternua"} /></span>
              <strong>{messengerExpanded ? <LocalizedText source={"Vos conversations"} /> : activeChatMeta?.displayName || <LocalizedText source={t("dock.discussions", "Discussions")} />}</strong>
              {messengerExpanded ? <span className="messenger-workspace-subtitle"><LocalizedText source={"Les liens se cultivent, un message à la fois."} /></span> : null}
            </div>
            <div className="friends-dock__chatpanel-actions">
              {messengerOnly && onMessengerExpandedChange ? <LocalizedElement elementType={"button"} copyAttributes={["aria-label","title"]} type="button"
                onClick={() => onMessengerExpandedChange(!messengerExpanded)}
                aria-label={messengerExpanded ? sourceAttribute("Réduire la messagerie") : sourceAttribute("Agrandir la messagerie")}
                title={messengerExpanded ? sourceAttribute("Réduire") : sourceAttribute("Agrandir")}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                  <rect x="3" y="4" width="18" height="16" rx="3" /><path d={messengerExpanded ? "M8 12h8" : "M8 12h8M12 8v8"} />
                </svg>
              </LocalizedElement> : null}
              {!messengerOnly || !activeChatUser ? <button type="button" className="friends-dock__panel-btn friends-dock__refresh" onClick={loadConversations}
                aria-label={t("common.refresh", "Actualiser")} title={t("common.refresh", "Actualiser")}>
                <svg className="friends-dock__refresh-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true"><path d="M20 7v5h-5M4 17v-5h5" /><path d="M6.1 6.1A8 8 0 0 1 20 12M4 12a8 8 0 0 0 13.9 5.9" /></svg>
                <span className="friends-dock__refresh-label">{<LocalizedText source={t("common.refresh", "Actualiser")} />}</span>
              </button> : null}
              <LocalizedElement elementType={"button"} copyAttributes={["aria-label"]}
                type="button"
                className="friends-dock__panel-btn friends-dock__panel-btn--ghost"
                onClick={() => setChatOpen(false)}
                aria-label={sourceAttribute("Fermer la messagerie")}
              >
                <span className="friends-dock__close-label">{<LocalizedText source={t("common.close", "Fermer")} />}</span>
                <span className="friends-dock__close-icon" aria-hidden="true">×</span>
              </LocalizedElement>
            </div>
          </div>

          {chatErr ? <div className="friends-dock__error">{chatErr}</div> : null}

          <div className="friends-dock__chatpanel-body">
            <LocalizedElement elementType={"div"} copyAttributes={["aria-label"]} className="friends-dock__chatlist" aria-label={sourceAttribute("Liste des discussions")}>
              {messengerExpanded ? <div className="messenger-workspace-list-head">
                <div><strong><LocalizedText source={"Discussions"} /></strong><span>{conversations.length}</span></div>
                <label className="messenger-workspace-search">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true"><circle cx="10.5" cy="10.5" r="6.5" /><path d="m16 16 5 5" /></svg>
                  <LocalizedElement elementType={"input"} copyAttributes={["aria-label","placeholder"]} ref={messengerSearchRef} type="search" aria-label={sourceAttribute("Rechercher une conversation")} placeholder={sourceAttribute("Rechercher une conversation…")}
                    value={conversationSearch} onChange={(event) => setConversationSearch(event.target.value)} />
                </label>
              </div> : null}
              <LocalizedElement elementType={"div"} copyAttributes={["aria-label"]} className="friends-dock__conversation-filters" aria-label={sourceAttribute("Filtres de conversations")}>
                {[
                  ["all", "Toutes"],
                  ["unread", "Non lus"],
                  ["friends", "Amis"],
                  ["groups", "Groupes"],
                  ["journeys", "Démarches en cours"],
                ].map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    className={conversationFilter === value ? "is-active" : ""}
                    aria-pressed={conversationFilter === value}
                    onClick={() => setConversationFilter(value)}
                  >
                    {<LocalizedText source={label} />}
                  </button>
                ))}
              </LocalizedElement>
              {conversationFilter !== "groups" && conversationCategories.length > 1 ? (
                <LocalizedElement elementType={"select"} copyAttributes={["aria-label"]}
                  className="friends-dock__conversation-category"
                  value={conversationCategory}
                  onChange={(event) => setConversationCategory(event.target.value)}
                  aria-label={sourceAttribute("Filtrer par rubrique")}
                >
                  <LocalizedElement elementType={"option"} copyChildren value="all">Toutes les rubriques</LocalizedElement>
                  {conversationCategories.map((category) => (
                    <option key={category} value={category}>{conversationCategoryLabel(category)}</option>
                  ))}
                </LocalizedElement>
              ) : null}
              {conversationFilter === "groups" ? (
                visibleActivityGroups.length ? visibleActivityGroups.map((group) => (
                  <button
                    key={group.id || group.slug}
                    type="button"
                    className="friends-dock__chatrow friends-dock__chatrow--group"
                    onClick={() => navigate(`/chat/room/${encodeURIComponent(group.slug)}`)}
                  >
                    <span className="friends-dock__group-avatar" aria-hidden="true"><EternuaIcon name="people" /></span>
                    <span className="friends-dock__chatrow-main">
                      <span className="friends-dock__chatrow-title">{group.name}</span>
                      <span className="friends-dock__chatrow-sub">
                        {group.activity?.type ? `${conversationCategoryLabel(group.activity.type)} · ` : ""}
                        {Number(group.members_count || 0)}<LocalizedText source={" participant(s) confirmé(s)"} /></span>
                    </span>
                  </button>
                )) : <div className="friends-dock__empty"><LocalizedText source={"Aucun groupe actif."} /></div>
              ) : visibleConversations.length === 0 ? (
                <div className="friends-dock__empty">
                  {conversationSearch ? <LocalizedText source={"Aucune conversation ne correspond à votre recherche."} /> : <LocalizedText source={"Aucune discussion pour l’instant."} />}
                  <div style={{ marginTop: 8 }}>
                    <div style={{ fontWeight: 900, marginBottom: 8 }}><LocalizedText source={"Commencer une discussion"} /></div>
                    {(Array.isArray(friends) ? friends : []).slice(0, 12).map((f) => (
                      <button
                        key={f.id || f.username}
                        type="button"
                        className={
                          "friends-dock__chatrow" +
                          (activeChatUser &&
                          f?.username &&
                          String(f.username).toLowerCase() ===
                            String(activeChatUser).toLowerCase()
                            ? " friends-dock__chatrow--active"
                            : "")
                        }
                        onClick={() => openChatWith(f.username)}
                      >
                        <Avatar user={f} />
                        <div className="friends-dock__chatrow-main">
                          <div className="friends-dock__chatrow-title">
                            {f.display_name || f.username || <LocalizedText source={"Amie"} />}
                          </div>
                          <div className="friends-dock__chatrow-sub">
                            {f.online_chat
                              ? <LocalizedText source={"En ligne sur le chat"} />
                              : f.online_video
                              ? "En ligne en video"
                              : <LocalizedText source={"Ouvrir la discussion"} />}
                          </div>
                        </div>
                      </button>
                    ))}
                    <div style={{ marginTop: 10 }}>
                      <button
                        type="button"
                        className="friends-dock__panel-btn"
                        onClick={() => navigate("/amis")}
                      ><LocalizedText source={"Voir tous mes amis"} /></button>
                    </div>
                  </div>
                </div>
              ) : (
                visibleConversations.map((c, index) => {
                  const other = c.other || {};
                  const uname = other.username || "";
                  const active = uname && activeChatUser && uname.toLowerCase() === activeChatUser.toLowerCase();
                  const preview = c.last_message?.preview || "";
                  const unread = Number(c?.unread_count || 0) || 0;
                  return (
                    <React.Fragment key={c.id}>
                    {index === 0 || visibleConversations[index - 1]?.category !== c.category ? (
                      <div className="friends-dock__conversation-section">
                        {conversationCategoryLabel(c.category)}
                      </div>
                    ) : null}
                    <div className="friends-dock__conversation-item">
                      <button
                        type="button"
                        className={"friends-dock__chatrow" + (active ? " friends-dock__chatrow--active" : "") + (unread > 0 ? " friends-dock__chatrow--unread" : "")}
                        onClick={() => {
                          if (active) {
                            setActiveChatUser(null);
                            setChatMessages([]);
                            closeChatSocket();
                            return;
                          }
                          openChatWith(uname);
                        }}
                      >
                        <Avatar user={other} />
                        <div className="friends-dock__chatrow-main">
                          <div className="friends-dock__chatrow-title">
                            {c?.settings?.pinned ? <LocalizedElement elementType={"span"} copyAttributes={["title"]} title={sourceAttribute("Conversation épinglée")}>📌 </LocalizedElement> : null}
                            {other?.is_favorite ? <span title="Contact favori">★ </span> : null}
                            {other.display_name || other.username || "Discussion"}
                          </div>
                          <div className="friends-dock__chatrow-sub">
                            {preview ? preview : <LocalizedText source={"Ouvrir la discussion"} />}
                          </div>
                        </div>
                        {unread > 0 ? (
                          <LocalizedElement elementType={"span"} copyAttributes={["aria-label"]} className="friends-dock__chatrow-badge" aria-label={sourceAttribute("Messages non lus")}>
                            {unread > 99 ? "99+" : unread}
                          </LocalizedElement>
                        ) : null}
                      </button>
                      <TrustCompactSummary data-et-copy="title" summary={other.trust_profile} compact title="Confiance" />
                    </div>
                    </React.Fragment>
                  );
                })
              )}
              {conversationFilter !== "groups" && friendSuggestions.length ? (
                <div className="friends-dock__suggestions">
                  <div className="friends-dock__conversation-section"><LocalizedText source={"Suggestions pour vous"} /></div>
                  {friendSuggestions.slice(0, 4).map((suggestion) => (
                    <div className="friends-dock__suggestion" key={suggestion.username}>
                      <Avatar user={suggestion} />
                      <div className="friends-dock__chatrow-main">
                        <strong>{suggestion.display_name || suggestion.username}</strong>
                        <small>{suggestion.reason}</small>
                        {suggestion.mutual_friends_count ? (
                          <small>{suggestion.mutual_friends_count} ami(s) en commun</small>
                        ) : null}
                      </div>
                      <MemberRelationshipAction username={suggestion.username} friendSource="general" />
                    </div>
                  ))}
                </div>
              ) : null}
            </LocalizedElement>

            <LocalizedElement elementType={"div"} copyAttributes={["aria-label"]} className="friends-dock__chatview" aria-label={sourceAttribute("Conversation")}>
              {activeChatUser ? (
                <>
                  <LocalizedElement elementType={"div"} copyAttributes={["aria-label"]} className="friends-dock__chattools" aria-label={sourceAttribute("Actions conversation")}>
                    {messengerOnly ? (
                      <LocalizedElement elementType={"button"} copyAttributes={["aria-label"]}
                        type="button"
                        className="friends-dock__chattools-back"
                        onClick={() => {
                          setActiveChatUser(null);
                          setChatMessages([]);
                          closeChatSocket();
                        }}
                        aria-label={sourceAttribute("Retour aux conversations")}
                      >
                        <span aria-hidden="true">←</span><LocalizedText source={"Conversations"} /></LocalizedElement>
                    ) : (
                      <div className="friends-dock__chattools-name">
                        {activeChatMeta?.displayName || activeChatUser}
                      </div>
                    )}
                    {messengerExpanded ? <strong className="messenger-workspace-contact">{activeChatMeta?.displayName || activeChatUser}</strong> : null}
                    <div className="friends-dock__chattools-actions">
                      <ConversationOptions {...(messengerOnly ? {
                        className: "friends-dock__conversation-options",
                        onClick: (event) => { if (event.target.closest("button")) event.currentTarget.open = false; },
                        onBlur: (event) => { if (!event.currentTarget.contains(event.relatedTarget)) event.currentTarget.open = false; },
                        onKeyDown: (event) => {
                          if (event.key === "Escape") {
                            event.preventDefault();
                            event.stopPropagation();
                            event.currentTarget.open = false;
                            event.currentTarget.querySelector("summary")?.focus();
                          }
                        },
                      } : {})}>
                      {messengerOnly ? <LocalizedElement elementType={"summary"} copyAttributes={["aria-label","title"]} aria-label={sourceAttribute("Options de la conversation")} title={sourceAttribute("Options de la conversation")}>···</LocalizedElement> : null}
                      <div className="friends-dock__conversation-options-list">
                      <button
                        type="button"
                        className="friends-dock__chattools-btn"
                        onClick={() => updateConversationSetting("pinned", !activeConversation?.settings?.pinned)}
                      >
                        <EternuaIcon name="pin" className="friends-dock__tool-icon" /><span>{activeConversation?.settings?.pinned ? <LocalizedText source={"Épinglée"} /> : <LocalizedText source={"Épingler"} />}</span>
                      </button>
                      <button
                        type="button"
                        className="friends-dock__chattools-btn"
                        onClick={() => updateConversationSetting("muted", !activeConversation?.settings?.muted)}
                      >
                        <EternuaIcon name="bell" className="friends-dock__tool-icon" /><span>{activeConversation?.settings?.muted ? <LocalizedText source={"Sourdine"} /> : <LocalizedText source={"Alertes"} />}</span>
                      </button>
                      <button
                        type="button"
                        className="friends-dock__chattools-btn"
                        onClick={() => updateConversationSetting("journey_active", !activeConversation?.settings?.journey_active)}
                      >
                        <EternuaIcon name="compass" className="friends-dock__tool-icon" /><span>{activeConversation?.settings?.journey_active ? <LocalizedText source={"Démarche en cours"} /> : <LocalizedText source={"Démarche"} />}</span>
                      </button>
                      <button
                        type="button"
                        className="friends-dock__chattools-btn"
                        onClick={() => updateConversationSetting("archived", true)}
                      >
                        <EternuaIcon name="archive" className="friends-dock__tool-icon" /><span><LocalizedText source={"Archiver"} /></span>
                      </button>
                      <button
                        type="button"
                        className="friends-dock__chattools-btn"
                        onClick={() => setAppointmentOpen((value) => !value)}
                      >
                        <EternuaIcon name="calendar" className="friends-dock__tool-icon" /><span><LocalizedText source={"Rendez-vous"} /></span>
                      </button>
                      <button
                        type="button"
                        className="friends-dock__chattools-btn"
                        onClick={() => navigate(`/relations?contact=${encodeURIComponent(activeChatUser)}`)}
                      >
                        <EternuaIcon name="people" className="friends-dock__tool-icon" /><span><LocalizedText source={"Relations"} /></span>
                      </button>
                      {!messengerOnly ? <button
                        type="button"
                        className="friends-dock__chattools-btn"
                        onClick={() =>
                          navigate(
                            `/annonces?contact=${encodeURIComponent(
                              String(activeChatMeta?.username || activeChatUser || "")
                            )}`
                          )
                        }
                      ><LocalizedText source={"📣 Annonces"} /></button> : null}
                      {!messengerOnly ? <button
                        type="button"
                        className="friends-dock__chattools-btn"
                        onClick={() =>
                          navigate(
                            `/profil/${encodeURIComponent(
                              String(activeChatMeta?.username || activeChatUser || "")
                            )}`
                          )
                        }
                      ><LocalizedText source={"👤 Voir profil"} /></button> : null}
                      {!messengerOnly ? <button
                        type="button"
                        className="friends-dock__chattools-btn"
                        onClick={() =>
                          sendCallInvite({
                            toUsername: String(activeChatMeta?.username || activeChatUser || ""),
                            kind: "private_chat",
                          })
                        }
                      ><LocalizedText source={"📞 Appel chat"} /></button> : null}
                      </div>
                      </ConversationOptions>
                      <button
                        type="button"
                        className="friends-dock__chattools-btn friends-dock__chattools-btn--audio"
                        onClick={() =>
                          sendCallInvite({
                            toUsername: String(activeChatMeta?.username || activeChatUser || ""),
                            kind: "private_video",
                            roomSlug: "audio",
                            openDirect: true,
                          })
                        }
                        aria-label={`Appeler ${activeChatMeta?.displayName || activeChatUser} en audio`}
                      >
                        <EternuaIcon name="phone" className="friends-dock__tool-icon" /><span><LocalizedText source={"Audio"} /></span>
                      </button>
                      <button
                        type="button"
                        className="friends-dock__chattools-btn friends-dock__chattools-btn--video"
                        onClick={() =>
                          sendCallInvite({
                            toUsername: String(activeChatMeta?.username || activeChatUser || ""),
                            kind: "private_video",
                            openDirect: true,
                          })
                        }
                      >
                        <EternuaIcon name="video" className="friends-dock__tool-icon" /><span><LocalizedText source={"Vidéo"} /></span>
                      </button>
                      {!messengerOnly ? <button
                        type="button"
                        className="friends-dock__chattools-btn friends-dock__chattools-btn--danger"
                        onClick={() =>
                          confirmDeleteDiscussion(
                            String(activeChatMeta?.username || activeChatUser || "")
                          )
                        }
                      ><LocalizedText source={"🗑 Supprimer discussion"} /></button> : null}
                      {!messengerOnly ? <button
                        type="button"
                        className="friends-dock__chattools-btn"
                        onClick={() => navigate("/jeux")}
                      ><LocalizedText source={"🎮 Ouvrir les jeux"} /></button> : null}
                    </div>
                  </LocalizedElement>
                  {activeConversation?.context?.url ? (
                    <div className="friends-dock__origin-context">
                      <div>
                        <strong>{activeConversation.context.title || conversationCategoryLabel(activeConversation.category)}</strong>
                        {activeConversation.context.message ? <span>{activeConversation.context.message}</span> : null}
                      </div>
                      <Link to={activeConversation.context.url}><LocalizedText source={"Revoir l’annonce"} /></Link>
                    </div>
                  ) : null}
                  {activeConversation?.relationship_kind !== "friend" && activeConversation?.temporary_access ? (
                    <div className="friends-dock__temporary-access" role="status">
                      <div>
                        <strong><LocalizedText source={"Conversation temporaire liée à cette publication"} /></strong>
                        <span>
                          {Number(activeConversation.temporary_access.remaining_messages || 0)}<LocalizedText source={" message(s) restant(s)."} />{activeConversation.temporary_access.requires_friendship
                            ? <LocalizedText source={" Ajoutez cette personne pour continuer."} />
                            : <LocalizedText source={" L’amitié permettra de poursuivre sans limite."} />}
                        </span>
                      </div>
                      <MemberRelationshipAction
                        username={activeChatUser}
                        friendSource={activeConversation?.context?.source || "general"}
                        friendContext={activeConversation?.context || {}}
                      />
                      {activeConversation.temporary_access.recipient_username === user?.username
                        && activeConversation.temporary_access.status === "active" ? (
                        <button type="button" onClick={declineTemporaryContact}><LocalizedText source={"Refuser silencieusement"} /></button>
                      ) : null}
                    </div>
                  ) : null}
                  {activeChatMeta?.awayStatus?.enabled ? (
                    <div className="friends-dock__away-status" role="status">
                      <strong><LocalizedText source={"Mode absence"} /></strong>
                      <span>{activeChatMeta.awayStatus.message}</span>
                      {activeChatMeta.awayStatus.ends_at ? <small><LocalizedText source={"Retour prévu : "} />{new Date(activeChatMeta.awayStatus.ends_at).toLocaleString(formattingLocale())}</small> : null}
                    </div>
                  ) : null}
                  {appointmentOpen ? (
                    <form className="friends-dock__appointment" onSubmit={proposeAppointment}>
                      <strong><LocalizedText source={"Proposer un rendez-vous dans un lieu public"} /></strong>
                      <label><LocalizedText source={"Date et heure"} /><input
                          type="datetime-local"
                          required
                          value={appointmentForm.scheduled_for}
                          onChange={(event) => setAppointmentForm((current) => ({
                            ...current,
                            scheduled_for: event.target.value,
                          }))}
                        />
                      </label>
                      <label><LocalizedText source={"Lieu public"} /><LocalizedElement elementType={"input"} copyAttributes={["placeholder"]}
                          required
                          minLength={3}
                          placeholder={sourceAttribute("Bibliothèque, café, gare…")}
                          value={appointmentForm.public_place_label}
                          onChange={(event) => setAppointmentForm((current) => ({
                            ...current,
                            public_place_label: event.target.value,
                          }))}
                        />
                      </label>
                      <label><LocalizedText source={"Message facultatif"} /><input
                          maxLength={500}
                          value={appointmentForm.note}
                          onChange={(event) => setAppointmentForm((current) => ({
                            ...current,
                            note: event.target.value,
                          }))}
                        />
                      </label>
                      <div>
                        <button type="button" onClick={() => setAppointmentOpen(false)}><LocalizedText source={"Annuler"} /></button>
                        <button type="submit" disabled={appointmentBusy}>
                          {appointmentBusy ? <LocalizedText source={"Envoi…"} /> : <LocalizedText source={"Proposer"} />}
                        </button>
                      </div>
                    </form>
                  ) : null}
                  <ChatPanel data-et-copy="subtitle,placeholder,emptyMessage"
                    titre={`Chat privé`}
                    hideHeader={messengerOnly}
                    compactTools={messengerOnly}
                    subtitle="Conversation privée entre vous et votre contact."
                    placeholder={displayAttribute("Écris un message…")}
                    emptyMessage="Aucun message pour l’instant."
                    messages={chatMessages}
                    envoyerMessage={sendChatMessage}
                    currentUsername={user?.username}
                  />
                </>
              ) : (
                messengerExpanded ? <div className="messenger-workspace-welcome">
                  <div className="messenger-workspace-mark" aria-hidden="true"><svg width="44" height="44" viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.2"><path d="M10 10h28a4 4 0 0 1 4 4v18a4 4 0 0 1-4 4H22l-12 7v-7a4 4 0 0 1-4-4V14a4 4 0 0 1 4-4Z" /><path d="M15 20h18M15 27h12" /></svg></div>
                  <span className="messenger-workspace-eyebrow"><LocalizedText source={"VOTRE ESPACE D’ÉCHANGE"} /></span>
                  <h2><LocalizedText source={"Une place pour"} /><br /><em><LocalizedText source={"vos conversations."} /></em></h2>
                  <p><LocalizedText source={"Retrouvez vos proches, partagez une idée,"} /><br /><LocalizedText source={"ou donnez suite à une belle rencontre."} /></p>
                  <button type="button" onClick={() => messengerSearchRef.current?.focus()}><LocalizedText source={"Choisir une conversation "} /><span aria-hidden="true">↗</span></button>
                  <div className="messenger-workspace-note"><LocalizedText source={"AMIS "} /><span>·</span><LocalizedText source={" RENCONTRES "} /><span>·</span><LocalizedText source={" PROJETS"} /></div>
                </div> : <div className="friends-dock__empty"><LocalizedText source={"Choisissez une personne pour ouvrir la conversation."} /></div>
              )}
            </LocalizedElement>
          </div>
        </div>
      ) : null}

      {!chatOpen && chatToast ? (
        <div className="friends-dock__toast" role="status" aria-label="Notification chat">
          <div className="friends-dock__toast-text">{chatToast}</div>
          <button type="button" className="friends-dock__toast-btn" onClick={() => setChatOpen(true)}><LocalizedText source={"Ouvrir"} /></button>
        </div>
      ) : null}

      {confirmDeleteChatOpen ? (
        <div className="friends-dock__delete-overlay" role="dialog" aria-modal="true">
          <div className="friends-dock__delete-card">
            <h3><LocalizedText source={"Supprimer la discussion ?"} /></h3>
            <p><LocalizedText source={"Cette action va effacer les messages avec"} />{" "}
              <strong>@{deleteChatTarget || activeChatUser}</strong>.
            </p>
            {deleteChatErr ? (
              <p className="friends-dock__delete-error">{deleteChatErr}</p>
            ) : null}
            <div className="friends-dock__delete-actions">
              <button
                type="button"
                className="cancel"
                disabled={deleteChatBusy}
                onClick={() => {
                  setConfirmDeleteChatOpen(false);
                  setDeleteChatErr("");
                }}
              ><LocalizedText source={"Annuler"} /></button>
              <button
                type="button"
                className="confirm"
                disabled={deleteChatBusy}
                onClick={deleteDiscussion}
              >
                {deleteChatBusy ? <LocalizedText source={"Suppression..."} /> : <LocalizedText source={"Confirmer"} />}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {!messengerOnly && open && isAuthed && (
        <LocalizedElement elementType={"div"} copyAttributes={["aria-label"]} className="friends-dock__panel" role="dialog" aria-label={sourceAttribute("Amis et demandes")}>
          <div className="friends-dock__panel-top">
            <div className="friends-dock__panel-title"><LocalizedText source={"Espace amis"} /></div>
            <div className="friends-dock__panel-actions">
              <button
                type="button"
                className="friends-dock__panel-btn"
                onClick={() => navigate("/amis")}
              ><LocalizedText source={"Page amis"} /></button>
              <button
                type="button"
                className="friends-dock__panel-btn friends-dock__panel-btn--ghost"
                onClick={() => {
                  setOpen(false);
                }}
              ><LocalizedText source={"Fermer"} /></button>
            </div>
          </div>

          {error ? <div className="friends-dock__error">{<LocalizedText source={error} />}</div> : null}

          {showCallsSection && (
            <div className="friends-dock__section" data-tab="calls">
              <div className="friends-dock__section-head">
                <div className="friends-dock__section-title"><LocalizedText source={"Appels"} /></div>
                <div className="friends-dock__section-tools">
                  <button
                    type="button"
                    className="friends-dock__link"
                    onClick={async () => {
                      setError("");
                      try {
                        const res = await fetch("/api/chat/calls/notifications/clear/", {
                          method: "POST",
                          headers: buildAuthHeaders(),
                        });
                        if (res.ok) {
                          setCallNotifs([]);
                          setIncomingCall(null);
                          setOutgoingCall(null);
                          stopIncomingRingtone();
                          setChatToast("Tous les appels ont été effacés.");
                          window.setTimeout(() => setChatToast(""), 2600);
                        } else {
                          const data = await res.json().catch(() => ({}));
                          setError(data?.detail || "Impossible d'effacer tous les appels.");
                        }
                      } catch (e) {
                        setError(e?.message || "Impossible d'effacer tous les appels.");
                      }
                      loadCallNotifs();
                    }}
                  ><LocalizedText source={"Effacer tout"} /></button>
                  <button
                    type="button"
                    className="friends-dock__link"
                    onClick={loadCallNotifs}
                  ><LocalizedText source={"Actualiser"} /></button>
                </div>
              </div>
              <div className="friends-dock__list friends-dock__list--calls">
                {callNotifs.length === 0 ? (
                  <div className="friends-dock__empty"><LocalizedText source={"Aucun appel recent."} /></div>
                ) : (
                  <>
                  <div className="friends-dock__subsection-title"><LocalizedText source={"Appels reçus"} /></div>
                  {receivedCalls.length === 0 ? (
                    <div className="friends-dock__empty"><LocalizedText source={"Aucun appel reçu."} /></div>
                  ) : receivedCalls.slice(0, 8).map((n) => {
                    const who =
                      n?.other_user?.display_name ||
                      n?.other_user?.username ||
                      n?.from_user?.display_name ||
                      n?.from_user?.username ||
                      "une amie";
                    const whenValue = n?.updated_at || n?.created_at;
                    const when = whenValue
                      ? new Date(whenValue).toLocaleString(formattingLocale(), {
                          day: "2-digit",
                          month: "short",
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      : "";
                    const statusLabel =
                      n?.status === "missed"
                        ? "Appel en absence"
                        : n?.status === "ringing"
                        ? "Invitation"
                        : n?.status === "accepted"
                        ? "Accepté"
                        : n?.status === "declined"
                        ? "Refusé"
                        : n?.status || "";

                    return (
                      <div className="friends-dock__row" key={`received-${n.id}`}>
                        <div className="friends-dock__row-main">
                          <div className="friends-dock__row-title">{who}</div>
                          <div className="friends-dock__row-sub">
                            {callKindLabel(n?.kind, n?.room_slug)} · {statusLabel}
                            {when ? ` · ${when}` : ""}
                          </div>
                        </div>
                        <div className="friends-dock__row-actions">
                          {n?.other_user?.username ? (
                            <Link
                              to={`/profil/${encodeURIComponent(n.other_user.username)}`}
                              className="friends-dock__btn"
                            ><LocalizedText source={"Profil"} /></Link>
                          ) : null}

                          {n?.join_url ? (
                            <button
                              type="button"
                              className="friends-dock__btn friends-dock__btn--primary"
                              onClick={async () => {
                                let acceptedJoinUrl = n.join_url;
                                try {
                                  const response = await fetch(
                                    `/api/chat/calls/notifications/${n.id}/accept/`,
                                    {
                                      method: "POST",
                                      headers: buildAuthHeaders(),
                                    }
                                  );
                                  const data = await response.json().catch(() => ({}));
                                  if (!response.ok) {
                                    throw new Error(
                                      data?.detail ||
                                        "L’appel n’a pas pu être accepté."
                                    );
                                  }
                                  if (data?.join_url) {
                                    acceptedJoinUrl = data.join_url;
                                  }
                                } catch (error) {
                                  setChatToast(
                                    error?.message ||
                                      "L’appel n’a pas pu être accepté. Réessaie."
                                  );
                                  window.setTimeout(
                                    () => setChatToast(""),
                                    4500
                                  );
                                  return;
                                }
                                if (!acceptedJoinUrl) return;
                                const separator = acceptedJoinUrl.includes("?")
                                  ? "&"
                                  : "?";
                                navigate(
                                  `${acceptedJoinUrl}${separator}accepted_call=${encodeURIComponent(
                                    n.id || ""
                                  )}`
                                );
                              }}
                            >
                              {n?.kind === "private_chat" ? <LocalizedText source={"Rejoindre le chat"} /> : <LocalizedText source={"Rejoindre l’appel vidéo"} />}
                            </button>
                          ) : null}

                          {!n?.seen ? (
                            <button
                              type="button"
                              className="friends-dock__btn"
                              onClick={async () => {
                                try {
                                  await fetch(
                                    `/api/chat/calls/notifications/${n.id}/seen/`,
                                    {
                                      method: "POST",
                                      headers: buildAuthHeaders(),
                                    }
                                  );
                                } catch (_) {
                                  // no-op
                                }
                                loadCallNotifs();
                              }}
                            ><LocalizedText source={"Vu"} /></button>
                          ) : null}
                          <button
                            type="button"
                            className="friends-dock__btn"
                            onClick={() => clearOneCallNotif(n)}
                          ><LocalizedText source={"Effacer"} /></button>
                        </div>
                      </div>
                    );
                  })}
                  <div className="friends-dock__subsection-title"><LocalizedText source={"Appels envoyés"} /></div>
                  {sentCalls.length === 0 ? (
                    <div className="friends-dock__empty"><LocalizedText source={"Aucun appel envoyé."} /></div>
                  ) : sentCalls.slice(0, 8).map((n) => {
                    const who =
                      n?.other_user?.display_name ||
                      n?.other_user?.username ||
                      n?.to_user?.display_name ||
                      n?.to_user?.username ||
                      "une amie";
                    const whenValue = n?.updated_at || n?.created_at;
                    const when = whenValue
                      ? new Date(whenValue).toLocaleString(formattingLocale(), {
                          day: "2-digit",
                          month: "short",
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      : "";
                    const statusLabel =
                      n?.status === "missed"
                        ? "Appel en absence"
                        : n?.status === "ringing"
                        ? "Invitation"
                        : n?.status === "accepted"
                        ? "Accepté"
                        : n?.status === "declined"
                        ? "Refusé"
                        : n?.status || "";

                    return (
                      <div className="friends-dock__row" key={`sent-${n.id}`}>
                        <div className="friends-dock__row-main">
                          <div className="friends-dock__row-title">{who}</div>
                          <div className="friends-dock__row-sub">
                            {callKindLabel(n?.kind, n?.room_slug)} · {statusLabel}
                            {when ? ` · ${when}` : ""}
                          </div>
                        </div>
                        <div className="friends-dock__row-actions">
                          {n?.other_user?.username ? (
                            <Link
                              to={`/profil/${encodeURIComponent(n.other_user.username)}`}
                              className="friends-dock__btn"
                            ><LocalizedText source={"Profil"} /></Link>
                          ) : null}
                          {String(n?.status || "") === "ringing" ? (
                            <span className="friends-dock__badge"><LocalizedText source={"En attente"} /></span>
                          ) : null}
                          <button
                            type="button"
                            className="friends-dock__btn"
                            onClick={() => clearOneCallNotif(n)}
                          ><LocalizedText source={"Effacer"} /></button>
                        </div>
                      </div>
                    );
                  })}
                  </>
                )}
              </div>
            </div>
          )}

          {showAcceptedSection && (
            <div className="friends-dock__section" data-tab="accepted">
              <div className="friends-dock__section-head">
                <div className="friends-dock__section-title"><LocalizedText source={"Amis acceptes"} /></div>
                <button
                  type="button"
                  className="friends-dock__link"
                  onClick={clearAccepted}
                ><LocalizedText source={"Effacer"} /></button>
              </div>
              <div className="friends-dock__list">
                {acceptedNotifs.length === 0 ? (
                  <div className="friends-dock__empty"><LocalizedText source={"Aucun nouvel ami."} /></div>
                ) : (
                  acceptedNotifs.slice(0, 8).map((n) => (
                    <div className="friends-dock__row" key={n.username + String(n.ts)}>
                      <div className="friends-dock__row-main">
                        <div className="friends-dock__row-title">@{n.username}</div>
                        <div className="friends-dock__row-sub"><LocalizedText source={"a accepte ta demande"} /></div>
                      </div>
                      <div className="friends-dock__row-actions">
                        <Link to={`/profil/${n.username}`} className="friends-dock__btn"><LocalizedText source={"Profil"} /></Link>
                        <Link
                          to={`/amis/salon/${n.username}`}
                          className="friends-dock__btn friends-dock__btn--primary"
                        ><LocalizedText source={"Salon"} /></Link>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {showRequestsSection && (
            <div className="friends-dock__section" data-tab="requests">
              <div className="friends-dock__section-head">
                <div className="friends-dock__section-title"><LocalizedText source={"Demandes recues"} /></div>
                <div className="friends-dock__section-badge">{incoming.length}</div>
              </div>
              <div className="friends-dock__list">
                {incoming.length === 0 ? (
                  <div className="friends-dock__empty"><LocalizedText source={"Aucune demande."} /></div>
                ) : (
                  incoming.slice(0, 8).map((r) => {
                    const disabled = busyIds.has(r.id);
                    return (
                      <div className="friends-dock__row" key={r.id}>
                        <div className="friends-dock__row-main">
                          <div className="friends-dock__row-title">@{r.from_username}</div>
                          <div className="friends-dock__row-sub"><LocalizedText source={"Rubrique: "} />{sourceLabel(r.source)}
                            {r?.context?.title ? ` · ${r.context.title}` : ""}
                          </div>
                          {r?.context_message || r?.context?.message ? (
                            <div className="friends-dock__request-context">« {r.context_message || r.context.message} »</div>
                          ) : null}
                          {r?.context?.url ? (
                            <Link className="friends-dock__request-link" to={r.context.url}><LocalizedText source={"Revoir l’annonce"} /></Link>
                          ) : null}
                        </div>
                        <div className="friends-dock__row-actions">
                          <Link
                            to={`/profil/${r.from_username}`}
                            className="friends-dock__btn"
                          ><LocalizedText source={"Profil"} /></Link>
                          <button
                            type="button"
                            className="friends-dock__btn friends-dock__btn--primary"
                            disabled={disabled}
                            onClick={() => respondRequest(r.id, "accept")}
                          ><LocalizedText source={"Accepter"} /></button>
                          <button
                            type="button"
                            className="friends-dock__btn friends-dock__btn--danger"
                            disabled={disabled}
                            onClick={() => respondRequest(r.id, "reject")}
                          ><LocalizedText source={"Refuser"} /></button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}

          <div className="friends-dock__section" data-tab="online">
            <div className="friends-dock__section-head">
              <div className="friends-dock__section-title"><LocalizedText source={"Amis connectes"} /></div>
              <div className="friends-dock__section-badge">{onlineFriends.length}</div>
            </div>
            <div className="friends-dock__list friends-dock__list--grid">
              {onlineFriends.length === 0 ? (
                <div className="friends-dock__empty"><LocalizedText source={"Aucun ami connecte."} /></div>
              ) : (
                onlineFriends.slice(0, 12).map((f) => (
                  <div
                    key={f.id || f.username}
                    className="friends-dock__friend"
                    title={`@${f.username}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate(`/profil/${encodeURIComponent(f.username)}`)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        navigate(`/profil/${encodeURIComponent(f.username)}`);
                      }
                    }}
                  >
                    <Avatar user={f} />
                    <div className="friends-dock__friend-meta">
                      <div className="friends-dock__friend-name">
                        {f.display_name || f.username}
                      </div>
                      <div className="friends-dock__friend-sub">
                        {f.online_chat ? "Chat" : ""}
                        {f.online_chat && f.online_video ? " + " : ""}
                        {f.online_video ? "Video" : ""}
                        {!f.online_chat && !f.online_video ? <LocalizedText source={"En ligne"} /> : ""}
                      </div>
                    </div>

                    <LocalizedElement elementType={"div"} copyAttributes={["aria-label"]}
                      className="friends-dock__friend-actions"
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                      aria-label={sourceAttribute("Actions rapides")}
                    >
                      <button
                        type="button"
                        className="friends-dock__mini-btn"
                        onClick={() =>
                          sendCallInvite({
                            toUsername: f.username,
                            kind: "private_chat",
                          })
                        }
                        aria-label={`Appel chat ${f.username}`}
                      >
                        📞
                      </button>

                      <button
                        type="button"
                        className="friends-dock__mini-btn"
                        onClick={() =>
                          sendCallInvite({
                            toUsername: f.username,
                            kind: "private_video",
                            openDirect: true,
                          })
                        }
                        aria-label={`Appeler ${f.username}`}
                      >
                        🎥
                      </button>

                      {currentVideoRoomSlug ? (
                        <LocalizedElement elementType={"button"} copyAttributes={["aria-label"]}
                          type="button"
                          className="friends-dock__mini-btn"
                          onClick={() =>
                            sendCallInvite({
                              toUsername: f.username,
                              kind: "video_room",
                              roomSlug: currentVideoRoomSlug,
                            })
                          }
                          aria-label={sourceAttribute(`Inviter ${f.username} dans la salle`, "Inviter __ET_VALUE_0__ dans la salle")}
                        >
                          ➕
                        </LocalizedElement>
                      ) : null}
                    </LocalizedElement>
                  </div>
                ))
              )}
            </div>

            <div className="friends-dock__footer-links">
              <button
                type="button"
                className="friends-dock__link"
                onClick={() => navigate("/amis")}
              ><LocalizedText source={"Voir tous les amis"} /></button>
              <button
                type="button"
                className="friends-dock__link"
                onClick={() => navigate("/amis/groupes")}
              ><LocalizedText source={"Groupe d'amie"} /></button>
              <button
                type="button"
                className="friends-dock__link"
                onClick={() => navigate("/parrainage")}
              ><LocalizedText source={"Parrainage"} /></button>
              <button type="button" className="friends-dock__link" onClick={load}><LocalizedText source={"Actualiser"} /></button>
              <button
                type="button"
                className="friends-dock__link"
                onClick={() => {
                  setOpen(false);
                }}
              ><LocalizedText source={"Fermer"} /></button>
            </div>
          </div>

          <div className="friends-dock__panel-bottom">
            <div className="friends-dock__hint"><LocalizedText source={"Scroll vers le bas: la barre footer se cache. Scroll vers le haut: elle revient."} /></div>
          </div>
        </LocalizedElement>
      )}
    </div>
  );
}
